import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GithubConnectError from './GithubConnectError';
import { useTranslation } from 'react-i18next';
import axios from '../api';
import './VendorCards.css';

/* ── Logo sources ──────────────────────────────────────────────
   cdn.simpleicons.org — confirmed working slugs (colored SVG)
   Wikipedia SVGs      — used for major vendors (full-color logos)
   FA brands fallback  — for vendors where CDN fails
────────────────────────────────────────────────────────────── */
const SI   = 'https://cdn.simpleicons.org';
const WIKI = 'https://upload.wikimedia.org/wikipedia/commons';
/* Clearbit Logo API — full-colour brand logos keyed by the vendor's domain. Used for vendors
   Simple Icons has dropped for trademark reasons (Ping/JumpCloud/OneLogin/Jamf/Duo/Intune, etc.).
   Broken/blocked requests degrade to the accent-letter tile via VendorIcon's onError handler. */
const CB   = (domain) => `https://www.google.com/s2/favicons?sz=64&domain=${domain}`;

const LOGO_URLS = {
  aws:          `${WIKI}/9/93/Amazon_Web_Services_Logo.svg`,
  azure:        `${WIKI}/f/fa/Microsoft_Azure.svg`,
  gcp:          `${WIKI}/5/51/Google_Cloud_logo.svg`,
  github:       `${SI}/github/181717`,
  gitlab:       `${SI}/gitlab/FC6D26`,
  jira:         `${SI}/jira/0052CC`,
  kubernetes:   `${SI}/kubernetes/326CE5`,
  datadog:      `${SI}/datadog/632CA6`,
  okta:         `${SI}/okta/007DC1`,
  googleworkspace: `${SI}/google/4285F4`,
  onelogin:     CB('onelogin.com'),
  jumpcloud:    CB('jumpcloud.com'),
  duo:          CB('duo.com'),
  pingone:      CB('pingidentity.com'),
  auth0:        `${SI}/auth0/EB5424`,
  vercel:       `${SI}/vercel/000000`,
  clerk:        `${SI}/clerk/6C47FF`,
  postmark:     CB('postmarkapp.com'),
  jamf:         CB('jamf.com'),
  intune:       CB('microsoft.com'),
  kandji:       CB('kandji.io'),
  snowflake:    `${SI}/snowflake/29B5E8`,
  splunk:       `${SI}/splunk/000000`,
  pagerduty:    `${SI}/pagerduty/06AC38`,
  bitbucket:    `${SI}/bitbucket/0052CC`,
  digitalocean: `${SI}/digitalocean/0080FF`,
  newrelic:     `${SI}/newrelic/008C99`,
  cloudflare:   `${SI}/cloudflare/F38020`,
  sentry:       `${SI}/sentry/362D59`,
  microsoft:    `${WIKI}/4/44/Microsoft_logo.svg`,
  /* slack / crowdstrike / servicenow — no working CDN URL; use FA fallback */
  /* ---- catalog evidence vendor logos (Clearbit; onError → letter-tile) ---- */
  "ascend": "https://www.google.com/s2/favicons?sz=64&domain=ascend.com",
  "assurancelab": "https://www.google.com/s2/favicons?sz=64&domain=assurancelab.com",
  "audora": "https://www.google.com/s2/favicons?sz=64&domain=audora.com",
  "fieldguide": "https://www.google.com/s2/favicons?sz=64&domain=fieldguide.com",
  "trenta": "https://www.google.com/s2/favicons?sz=64&domain=trenta.com",
  "certn": "https://www.google.com/s2/favicons?sz=64&domain=certn.com",
  "checkr": "https://www.google.com/s2/favicons?sz=64&domain=checkr.com",
  "hirepass": "https://www.google.com/s2/favicons?sz=64&domain=hirepass.com",
  "veremark": "https://www.google.com/s2/favicons?sz=64&domain=veremark.com",
  "vetty": "https://www.google.com/s2/favicons?sz=64&domain=vetty.com",
  "zinc": "https://www.google.com/s2/favicons?sz=64&domain=zinc.com",
  "capsule": "https://www.google.com/s2/favicons?sz=64&domain=capsule.com",
  "close": "https://www.google.com/s2/favicons?sz=64&domain=close.com",
  "copper": "https://www.google.com/s2/favicons?sz=64&domain=copper.com",
  "hubspot": "https://www.google.com/s2/favicons?sz=64&domain=hubspot.com",
  "insightly": "https://www.google.com/s2/favicons?sz=64&domain=insightly.com",
  "salesforce": "https://www.google.com/s2/favicons?sz=64&domain=salesforce.com",
  "zendesk": "https://www.google.com/s2/favicons?sz=64&domain=zendesk.com",
  "amazonecr": "https://www.google.com/s2/favicons?sz=64&domain=aws.amazon.com",
  "awsgovcloud": "https://www.google.com/s2/favicons?sz=64&domain=aws.amazon.com",
  "digitaloceanspaces": "https://www.google.com/s2/favicons?sz=64&domain=digitalocean.com",
  "gcpartifact": "https://www.google.com/s2/favicons?sz=64&domain=cloud.google.com",
  "grafanacloud": "https://www.google.com/s2/favicons?sz=64&domain=grafana.com",
  "heroku": "https://www.google.com/s2/favicons?sz=64&domain=heroku.com",
  "netlify": "https://www.google.com/s2/favicons?sz=64&domain=netlify.com",
  "oci": "https://www.google.com/s2/favicons?sz=64&domain=oracle.com",
  "ovhcloud": "https://www.google.com/s2/favicons?sz=64&domain=ovhcloud.com",
  "railway": "https://www.google.com/s2/favicons?sz=64&domain=railway.app",
  "render": "https://www.google.com/s2/favicons?sz=64&domain=render.com",
  "scaleway": "https://www.google.com/s2/favicons?sz=64&domain=scaleway.com",
  "intercom": "https://www.google.com/s2/favicons?sz=64&domain=intercom.com",
  "zoom": "https://www.google.com/s2/favicons?sz=64&domain=zoom.us",
  "databricks": "https://www.google.com/s2/favicons?sz=64&domain=databricks.com",
  "mongoatlas": "https://www.google.com/s2/favicons?sz=64&domain=mongodb.com",
  "box": "https://www.google.com/s2/favicons?sz=64&domain=box.com",
  "dropbox": "https://www.google.com/s2/favicons?sz=64&domain=dropbox.com",
  "googledrive": "https://www.google.com/s2/favicons?sz=64&domain=google.com",
  "guru": "https://www.google.com/s2/favicons?sz=64&domain=guru.com",
  "microsoftsharepoint": "https://www.google.com/s2/favicons?sz=64&domain=microsoft.com",
  "notion": "https://www.google.com/s2/favicons?sz=64&domain=notion.so",
  "pima": "https://www.google.com/s2/favicons?sz=64&domain=pima.com",
  "agency": "https://www.google.com/s2/favicons?sz=64&domain=agency.com",
  "automox": "https://www.google.com/s2/favicons?sz=64&domain=automox.com",
  "hexnode": "https://www.google.com/s2/favicons?sz=64&domain=hexnode.com",
  "kitecyber": "https://www.google.com/s2/favicons?sz=64&domain=kitecyber.com",
  "7shifts": "https://www.google.com/s2/favicons?sz=64&domain=7shifts.com",
  "adpworkforce": "https://www.google.com/s2/favicons?sz=64&domain=adp.com",
  "alexishr": "https://www.google.com/s2/favicons?sz=64&domain=alexishr.com",
  "alterapayroll": "https://www.google.com/s2/favicons?sz=64&domain=alterapayroll.com",
  "bamboohr": "https://www.google.com/s2/favicons?sz=64&domain=bamboohr.com",
  "bob": "https://www.google.com/s2/favicons?sz=64&domain=bob.com",
  "breathe": "https://www.google.com/s2/favicons?sz=64&domain=breathe.com",
  "breezyhr": "https://www.google.com/s2/favicons?sz=64&domain=breezyhr.com",
  "breezyhrx": "https://www.google.com/s2/favicons?sz=64&domain=breezyhr.com",
  "central": "https://www.google.com/s2/favicons?sz=64&domain=central.com",
  "dayforce": "https://www.google.com/s2/favicons?sz=64&domain=dayforce.com",
  "charliehr": "https://www.google.com/s2/favicons?sz=64&domain=charliehr.com",
  "clayhr": "https://www.google.com/s2/favicons?sz=64&domain=clayhr.com",
  "cyberark": "https://www.google.com/s2/favicons?sz=64&domain=cyberark.com",
  "deel": "https://www.google.com/s2/favicons?sz=64&domain=deel.com",
  "employmenthero": "https://www.google.com/s2/favicons?sz=64&domain=employmenthero.com",
  "factorial": "https://www.google.com/s2/favicons?sz=64&domain=factorial.com",
  "freshteam": "https://www.google.com/s2/favicons?sz=64&domain=freshworks.com",
  "gusto": "https://www.google.com/s2/favicons?sz=64&domain=gusto.com",
  "hrcloud": "https://www.google.com/s2/favicons?sz=64&domain=hrcloud.com",
  "hrpartner": "https://www.google.com/s2/favicons?sz=64&domain=hrpartner.com",
  "humaans": "https://www.google.com/s2/favicons?sz=64&domain=humaans.com",
  "insperity": "https://www.google.com/s2/favicons?sz=64&domain=insperity.com",
  "intellihr": "https://www.google.com/s2/favicons?sz=64&domain=intellihr.com",
  "isolved": "https://www.google.com/s2/favicons?sz=64&domain=isolved.com",
  "justworks": "https://www.google.com/s2/favicons?sz=64&domain=justworks.com",
  "kallidus": "https://www.google.com/s2/favicons?sz=64&domain=kallidus.com",
  "keka": "https://www.google.com/s2/favicons?sz=64&domain=keka.com",
  "kenjo": "https://www.google.com/s2/favicons?sz=64&domain=kenjo.com",
  "lano": "https://www.google.com/s2/favicons?sz=64&domain=lano.com",
  "lucca": "https://www.google.com/s2/favicons?sz=64&domain=lucca.fr",
  "namely": "https://www.google.com/s2/favicons?sz=64&domain=namely.com",
  "netsuite": "https://www.google.com/s2/favicons?sz=64&domain=netsuite.com",
  "nmbrs": "https://www.google.com/s2/favicons?sz=64&domain=nmbrs.com",
  "officient": "https://www.google.com/s2/favicons?sz=64&domain=officient.com",
  "oraclecloudhcm": "https://www.google.com/s2/favicons?sz=64&domain=oracle.com",
  "oyster": "https://www.google.com/s2/favicons?sz=64&domain=oysterhr.com",
  "paycaptain": "https://www.google.com/s2/favicons?sz=64&domain=paycaptain.com",
  "paychexflex": "https://www.google.com/s2/favicons?sz=64&domain=paychex.com",
  "paycor": "https://www.google.com/s2/favicons?sz=64&domain=paycor.com",
  "payfit95f52": "https://www.google.com/s2/favicons?sz=64&domain=payfit.com",
  "paylocity": "https://www.google.com/s2/favicons?sz=64&domain=paylocity.com",
  "payworks": "https://www.google.com/s2/favicons?sz=64&domain=payworks.com",
  "peoplehr": "https://www.google.com/s2/favicons?sz=64&domain=peoplehr.com",
  "personio": "https://www.google.com/s2/favicons?sz=64&domain=personio.com",
  "proliant": "https://www.google.com/s2/favicons?sz=64&domain=proliant.com",
  "quickbooks": "https://www.google.com/s2/favicons?sz=64&domain=intuit.com",
  "remote": "https://www.google.com/s2/favicons?sz=64&domain=remote.com",
  "rippling": "https://www.google.com/s2/favicons?sz=64&domain=rippling.com",
  "sage": "https://www.google.com/s2/favicons?sz=64&domain=sage.com",
  "sagehr": "https://www.google.com/s2/favicons?sz=64&domain=sage.com",
  "sapsuccessfactors": "https://www.google.com/s2/favicons?sz=64&domain=sap.com",
  "sesame": "https://www.google.com/s2/favicons?sz=64&domain=sesame.com",
  "squarepayrollf5639": "https://www.google.com/s2/favicons?sz=64&domain=squarepayroll.com",
  "torii": "https://www.google.com/s2/favicons?sz=64&domain=torii.com",
  "trinet": "https://www.google.com/s2/favicons?sz=64&domain=trinet.com",
  "ukgdimensions": "https://www.google.com/s2/favicons?sz=64&domain=ukg.com",
  "ukgpro": "https://www.google.com/s2/favicons?sz=64&domain=ukg.com",
  "ukgready": "https://www.google.com/s2/favicons?sz=64&domain=ukg.com",
  "workable": "https://www.google.com/s2/favicons?sz=64&domain=workable.com",
  "workday": "https://www.google.com/s2/favicons?sz=64&domain=workday.com",
  "zenefits": "https://www.google.com/s2/favicons?sz=64&domain=zenefits.com",
  "azuread": "https://www.google.com/s2/favicons?sz=64&domain=microsoft.com",
  "office365": "https://www.google.com/s2/favicons?sz=64&domain=microsoft.com",
  "entragcchigh": "https://www.google.com/s2/favicons?sz=64&domain=microsoft.com",
  "sclaeai": "https://www.google.com/s2/favicons?sz=64&domain=scale.com",
  "breachrx": "https://www.google.com/s2/favicons?sz=64&domain=breachrx.com",
  "incident": "https://www.google.com/s2/favicons?sz=64&domain=incident.io",
  "addigy": "https://www.google.com/s2/favicons?sz=64&domain=addigy.com",
  "datto": "https://www.google.com/s2/favicons?sz=64&domain=datto.com",
  "edamametechnologies": "https://www.google.com/s2/favicons?sz=64&domain=edamame.technology",
  "fleet": "https://www.google.com/s2/favicons?sz=64&domain=fleetdm.com",
  "microsoftintune": "https://www.google.com/s2/favicons?sz=64&domain=microsoft.com",
  "iru": "https://www.google.com/s2/favicons?sz=64&domain=iru.com",
  "kolide": "https://www.google.com/s2/favicons?sz=64&domain=kolide.com",
  "manageengine": "https://www.google.com/s2/favicons?sz=64&domain=manageengine.com",
  "microsoftendpointmanager": "https://www.google.com/s2/favicons?sz=64&domain=microsoft.com",
  "intunegcchigh": "https://www.google.com/s2/favicons?sz=64&domain=microsoft.com",
  "miradore": "https://www.google.com/s2/favicons?sz=64&domain=miradore.com",
  "ninjaone": "https://www.google.com/s2/favicons?sz=64&domain=ninjaone.com",
  "omnissa": "https://www.google.com/s2/favicons?sz=64&domain=omnissa.com",
  "primo": "https://www.google.com/s2/favicons?sz=64&domain=primo.com",
  "simplemdm": "https://www.google.com/s2/favicons?sz=64&domain=simplemdm.com",
  "swif": "https://www.google.com/s2/favicons?sz=64&domain=swif.com",
  "trio": "https://www.google.com/s2/favicons?sz=64&domain=trio.com",
  "vmwareworkspaceone": "https://www.google.com/s2/favicons?sz=64&domain=vmware.com",
  "xcitium": "https://www.google.com/s2/favicons?sz=64&domain=xcitium.com",
  "xfa": "https://www.google.com/s2/favicons?sz=64&domain=xfa.com",
  "sumologic": "https://www.google.com/s2/favicons?sz=64&domain=sumologic.com",
  "1password": "https://www.google.com/s2/favicons?sz=64&domain=1password.com",
  "4me": "https://www.google.com/s2/favicons?sz=64&domain=4me.com",
  "8x8": "https://www.google.com/s2/favicons?sz=64&domain=8x8.com",
  "abv": "https://www.google.com/s2/favicons?sz=64&domain=abv.com",
  "accessowl": "https://www.google.com/s2/favicons?sz=64&domain=accessowl.com",
  "acunetix360": "https://www.google.com/s2/favicons?sz=64&domain=acunetix360.com",
  "adobelearning": "https://www.google.com/s2/favicons?sz=64&domain=adobelearningmanager.com",
  "airtable": "https://www.google.com/s2/favicons?sz=64&domain=airtable.com",
  "akamai": "https://www.google.com/s2/favicons?sz=64&domain=akamai.com",
  "alertmedia": "https://www.google.com/s2/favicons?sz=64&domain=alertmedia.com",
  "amplitude": "https://www.google.com/s2/favicons?sz=64&domain=amplitude.com",
  "anthropic": "https://www.google.com/s2/favicons?sz=64&domain=anthropic.com",
  "apollo": "https://www.google.com/s2/favicons?sz=64&domain=apollo.com",
  "appaegis76b4c": "https://www.google.com/s2/favicons?sz=64&domain=appaegis.com",
  "applebusinessmanager": "https://www.google.com/s2/favicons?sz=64&domain=apple.com",
  "appranix": "https://www.google.com/s2/favicons?sz=64&domain=appranix.com",
  "ashby": "https://www.google.com/s2/favicons?sz=64&domain=ashbyhq.com",
  "assetpanda": "https://www.google.com/s2/favicons?sz=64&domain=assetpanda.com",
  "axisatmos": "https://www.google.com/s2/favicons?sz=64&domain=atmos.com",
  "autodesk": "https://www.google.com/s2/favicons?sz=64&domain=autodesk.com",
  "azuresynapse": "https://www.google.com/s2/favicons?sz=64&domain=microsoft.com",
  "bis": "https://www.google.com/s2/favicons?sz=64&domain=bis.com",
  "bitwarden": "https://www.google.com/s2/favicons?sz=64&domain=bitwarden.com",
  "blink": "https://www.google.com/s2/favicons?sz=64&domain=blink.com",
  "blinq": "https://www.google.com/s2/favicons?sz=64&domain=blinq.com",
  "blogin": "https://www.google.com/s2/favicons?sz=64&domain=blogin.com",
  "bluejeans": "https://www.google.com/s2/favicons?sz=64&domain=bluejeans.com",
  "bocada": "https://www.google.com/s2/favicons?sz=64&domain=bocada.com",
  "brex": "https://www.google.com/s2/favicons?sz=64&domain=brex.com",
  "britive": "https://www.google.com/s2/favicons?sz=64&domain=britive.com",
  "browserstack": "https://www.google.com/s2/favicons?sz=64&domain=browserstack.com",
  "calendly": "https://www.google.com/s2/favicons?sz=64&domain=calendly.com",
  "cats": "https://www.google.com/s2/favicons?sz=64&domain=cats.com",
  "charthop": "https://www.google.com/s2/favicons?sz=64&domain=charthop.com",
  "checkproof": "https://www.google.com/s2/favicons?sz=64&domain=checkproof.com",
  "cinode": "https://www.google.com/s2/favicons?sz=64&domain=cinode.com",
  "cleanmailf734a": "https://www.google.com/s2/favicons?sz=64&domain=cleanmail.com",
  "clebex": "https://www.google.com/s2/favicons?sz=64&domain=clebex.com",
  "clockworkrecruiter": "https://www.google.com/s2/favicons?sz=64&domain=clockworkrecruiting.com",
  "cloudback": "https://www.google.com/s2/favicons?sz=64&domain=cloudback.com",
  "coda": "https://www.google.com/s2/favicons?sz=64&domain=coda.com",
  "comeet": "https://www.google.com/s2/favicons?sz=64&domain=comeet.com",
  "commonpaper": "https://www.google.com/s2/favicons?sz=64&domain=commonpaper.com",
  "confluence": "https://www.google.com/s2/favicons?sz=64&domain=atlassian.com",
  "contentful": "https://www.google.com/s2/favicons?sz=64&domain=contentful.com",
  "continuitystrength": "https://www.google.com/s2/favicons?sz=64&domain=continuitystrength.com",
  "cside": "https://www.google.com/s2/favicons?sz=64&domain=cside.com",
  "cybsafe": "https://www.google.com/s2/favicons?sz=64&domain=cybsafe.com",
  "dashlane": "https://www.google.com/s2/favicons?sz=64&domain=dashlane.com",
  "dbtlab": "https://www.google.com/s2/favicons?sz=64&domain=dbtlabs.com",
  "dockerhub": "https://www.google.com/s2/favicons?sz=64&domain=docker.com",
  "documo": "https://www.google.com/s2/favicons?sz=64&domain=documo.com",
  "docusign": "https://www.google.com/s2/favicons?sz=64&domain=docusign.com",
  "doppler": "https://www.google.com/s2/favicons?sz=64&domain=doppler.com",
  "drift": "https://www.google.com/s2/favicons?sz=64&domain=drift.com",
  "dynatrace": "https://www.google.com/s2/favicons?sz=64&domain=dynatrace.com",
  "elastic": "https://www.google.com/s2/favicons?sz=64&domain=elastic.co",
  "eletive": "https://www.google.com/s2/favicons?sz=64&domain=eletive.com",
  "elium": "https://www.google.com/s2/favicons?sz=64&domain=elium.com",
  "envkey": "https://www.google.com/s2/favicons?sz=64&domain=envkey.com",
  "envoy": "https://www.google.com/s2/favicons?sz=64&domain=envoy.com",
  "evervault": "https://www.google.com/s2/favicons?sz=64&domain=evervault.com",
  "exium": "https://www.google.com/s2/favicons?sz=64&domain=exium.com",
  "faceup": "https://www.google.com/s2/favicons?sz=64&domain=faceup.com",
  "figma": "https://www.google.com/s2/favicons?sz=64&domain=figma.com",
  "fivetran": "https://www.google.com/s2/favicons?sz=64&domain=fivetran.com",
  "flock": "https://www.google.com/s2/favicons?sz=64&domain=flock.com",
  "foodee": "https://www.google.com/s2/favicons?sz=64&domain=foodee.com",
  "fourfour": "https://www.google.com/s2/favicons?sz=64&domain=fourfour.com",
  "gdprlocal": "https://www.google.com/s2/favicons?sz=64&domain=gdprlocal.com",
  "ghostable": "https://www.google.com/s2/favicons?sz=64&domain=ghostable.com",
  "gong": "https://www.google.com/s2/favicons?sz=64&domain=gong.com",
  "grammarly": "https://www.google.com/s2/favicons?sz=64&domain=grammarly.com",
  "greenhouse": "https://www.google.com/s2/favicons?sz=64&domain=greenhouse.io",
  "griffo": "https://www.google.com/s2/favicons?sz=64&domain=griffo.com",
  "h5mag": "https://www.google.com/s2/favicons?sz=64&domain=h5mag.com",
  "harness": "https://www.google.com/s2/favicons?sz=64&domain=harness.io",
  "harvest": "https://www.google.com/s2/favicons?sz=64&domain=harvest.com",
  "helloid": "https://www.google.com/s2/favicons?sz=64&domain=helloid.com",
  "hootsuite": "https://www.google.com/s2/favicons?sz=64&domain=hootsuite.com",
  "hoxhunt": "https://www.google.com/s2/favicons?sz=64&domain=hoxhunt.com",
  "ideagen": "https://www.google.com/s2/favicons?sz=64&domain=ideagen.com",
  "ideo": "https://www.google.com/s2/favicons?sz=64&domain=ideo.com",
  "invision": "https://www.google.com/s2/favicons?sz=64&domain=invision.com",
  "jobadder": "https://www.google.com/s2/favicons?sz=64&domain=jobadder.com",
  "jobvite": "https://www.google.com/s2/favicons?sz=64&domain=jobvite.com",
  "jostle": "https://www.google.com/s2/favicons?sz=64&domain=jostle.com",
  "junojourney": "https://www.google.com/s2/favicons?sz=64&domain=junojourney.com",
  "keeper": "https://www.google.com/s2/favicons?sz=64&domain=keepersecurity.com",
  "kpifire": "https://www.google.com/s2/favicons?sz=64&domain=kpifire.com",
  "lastpass": "https://www.google.com/s2/favicons?sz=64&domain=lastpass.com",
  "lattice": "https://www.google.com/s2/favicons?sz=64&domain=lattice.com",
  "latticeflowai": "https://www.google.com/s2/favicons?sz=64&domain=latticeflowai.com",
  "launchdarkly": "https://www.google.com/s2/favicons?sz=64&domain=launchdarkly.com",
  "leapsome": "https://www.google.com/s2/favicons?sz=64&domain=leapsome.com",
  "lever": "https://www.google.com/s2/favicons?sz=64&domain=lever.co",
  "logicgate": "https://www.google.com/s2/favicons?sz=64&domain=logicgate.com",
  "loom": "https://www.google.com/s2/favicons?sz=64&domain=loom.com",
  "looop": "https://www.google.com/s2/favicons?sz=64&domain=looop.com",
  "lucid": "https://www.google.com/s2/favicons?sz=64&domain=lucid.com",
  "appaegis": "https://www.google.com/s2/favicons?sz=64&domain=appaegis.com",
  "maptician": "https://www.google.com/s2/favicons?sz=64&domain=maptician.com",
  "metapulse": "https://www.google.com/s2/favicons?sz=64&domain=metapulse.com",
  "mindtickle": "https://www.google.com/s2/favicons?sz=64&domain=mindtickle.com",
  "miro": "https://www.google.com/s2/favicons?sz=64&domain=miro.com",
  "mixpanel": "https://www.google.com/s2/favicons?sz=64&domain=mixpanel.com",
  "mode": "https://www.google.com/s2/favicons?sz=64&domain=mode.com",
  "multiplier": "https://www.google.com/s2/favicons?sz=64&domain=usemultiplier.com",
  "mypolicies": "https://www.google.com/s2/favicons?sz=64&domain=mypolicies.com",
  "nordpass": "https://www.google.com/s2/favicons?sz=64&domain=nordpass.com",
  "openai": "https://www.google.com/s2/favicons?sz=64&domain=openai.com",
  "opsbook": "https://www.google.com/s2/favicons?sz=64&domain=opsbook.com",
  "opsgenie1f68e": "https://www.google.com/s2/favicons?sz=64&domain=opsgenie.com",
  "opslevel": "https://www.google.com/s2/favicons?sz=64&domain=opslevel.com",
  "osano": "https://www.google.com/s2/favicons?sz=64&domain=osano.com",
  "parsable": "https://www.google.com/s2/favicons?sz=64&domain=parsable.com",
  "peakon": "https://www.google.com/s2/favicons?sz=64&domain=peakon.com",
  "peripass": "https://www.google.com/s2/favicons?sz=64&domain=peripass.com",
  "phalanx": "https://www.google.com/s2/favicons?sz=64&domain=phalanx.com",
  "pipedrive": "https://www.google.com/s2/favicons?sz=64&domain=pipedrive.com",
  "postman": "https://www.google.com/s2/favicons?sz=64&domain=postman.com",
  "prioritymatrix": "https://www.google.com/s2/favicons?sz=64&domain=prioritymatrix.com",
  "probackupbv": "https://www.google.com/s2/favicons?sz=64&domain=probackupbv.com",
  "prodpad": "https://www.google.com/s2/favicons?sz=64&domain=prodpad.com",
  "productboard": "https://www.google.com/s2/favicons?sz=64&domain=productboard.com",
  "qlik": "https://www.google.com/s2/favicons?sz=64&domain=qlik.com",
  "ramp": "https://www.google.com/s2/favicons?sz=64&domain=ramp.com",
  "ravenna": "https://www.google.com/s2/favicons?sz=64&domain=ravenna.com",
  "reportit": "https://www.google.com/s2/favicons?sz=64&domain=reportit.com",
  "rfpio": "https://www.google.com/s2/favicons?sz=64&domain=rfpio.com",
  "retool": "https://www.google.com/s2/favicons?sz=64&domain=retool.com",
  "robin": "https://www.google.com/s2/favicons?sz=64&domain=robin.com",
  "rockset": "https://www.google.com/s2/favicons?sz=64&domain=rockset.com",
  "rollbar": "https://www.google.com/s2/favicons?sz=64&domain=rollbar.com",
  "scaleai": "https://www.google.com/s2/favicons?sz=64&domain=scale.com",
  "securily": "https://www.google.com/s2/favicons?sz=64&domain=securily.com",
  "segment": "https://www.google.com/s2/favicons?sz=64&domain=segment.com",
  "shopify": "https://www.google.com/s2/favicons?sz=64&domain=shopify.com",
  "sigma": "https://www.google.com/s2/favicons?sz=64&domain=sigmacomputing.com",
  "simplebackups": "https://www.google.com/s2/favicons?sz=64&domain=simplebackups.com",
  "slab": "https://www.google.com/s2/favicons?sz=64&domain=slab.com",
  "smartfile": "https://www.google.com/s2/favicons?sz=64&domain=smartfile.com",
  "smartrecruiters": "https://www.google.com/s2/favicons?sz=64&domain=smartrecruiters.com",
  "sophos": "https://www.google.com/s2/favicons?sz=64&domain=sophos.com",
  "sparkhire": "https://www.google.com/s2/favicons?sz=64&domain=sparkhire.com",
  "speakupnow": "https://www.google.com/s2/favicons?sz=64&domain=speakupnow.com",
  "splashtop": "https://www.google.com/s2/favicons?sz=64&domain=splashtop.com",
  "statsig": "https://www.google.com/s2/favicons?sz=64&domain=statsig.com",
  "subrosa": "https://www.google.com/s2/favicons?sz=64&domain=subrosa.com",
  "supabase": "https://www.google.com/s2/favicons?sz=64&domain=supabase.com",
  "sym": "https://www.google.com/s2/favicons?sz=64&domain=sym.com",
  "tailscale": "https://www.google.com/s2/favicons?sz=64&domain=tailscale.com",
  "talentech": "https://www.google.com/s2/favicons?sz=64&domain=talentech.com",
  "teamtailor": "https://www.google.com/s2/favicons?sz=64&domain=teamtailor.com",
  "termius": "https://www.google.com/s2/favicons?sz=64&domain=termius.com",
  "timetoreply": "https://www.google.com/s2/favicons?sz=64&domain=timetoreply.com",
  "torq": "https://www.google.com/s2/favicons?sz=64&domain=torq.com",
  "travelperk": "https://www.google.com/s2/favicons?sz=64&domain=travelperk.com",
  "trelica": "https://www.google.com/s2/favicons?sz=64&domain=trelica.com",
  "tribeloo": "https://www.google.com/s2/favicons?sz=64&domain=tribeloo.com",
  "twingate": "https://www.google.com/s2/favicons?sz=64&domain=twingate.com",
  "unifi": "https://www.google.com/s2/favicons?sz=64&domain=unifi.com",
  "vendr": "https://www.google.com/s2/favicons?sz=64&domain=vendr.com",
  "vonage": "https://www.google.com/s2/favicons?sz=64&domain=vonage.com",
  "webex": "https://www.google.com/s2/favicons?sz=64&domain=webex.com",
  "whimsical": "https://www.google.com/s2/favicons?sz=64&domain=whimsical.com",
  "workplace": "https://www.google.com/s2/favicons?sz=64&domain=workplace.com",
  "xero": "https://www.google.com/s2/favicons?sz=64&domain=xero.com",
  "zapier": "https://www.google.com/s2/favicons?sz=64&domain=zapier.com",
  "zavanta": "https://www.google.com/s2/favicons?sz=64&domain=zavanta.com",
  "zero": "https://www.google.com/s2/favicons?sz=64&domain=zero.com",
  "zip": "https://www.google.com/s2/favicons?sz=64&domain=zip.com",
  "zoominfo": "https://www.google.com/s2/favicons?sz=64&domain=zoominfo.com",
  "360learning": "https://www.google.com/s2/favicons?sz=64&domain=360learning.com",
  "adaptivesecurity": "https://www.google.com/s2/favicons?sz=64&domain=adaptivesecurity.com",
  "awarego": "https://www.google.com/s2/favicons?sz=64&domain=awarego.com",
  "caniphish": "https://www.google.com/s2/favicons?sz=64&domain=caniphish.com",
  "cybeready": "https://www.google.com/s2/favicons?sz=64&domain=cybeready.com",
  "cyberly": "https://www.google.com/s2/favicons?sz=64&domain=cyberly.com",
  "easyllama": "https://www.google.com/s2/favicons?sz=64&domain=easyllama.com",
  "elba": "https://www.google.com/s2/favicons?sz=64&domain=elba.com",
  "ethena": "https://www.google.com/s2/favicons?sz=64&domain=ethena.com",
  "ghosteye": "https://www.google.com/s2/favicons?sz=64&domain=ghosteye.com",
  "haekka": "https://www.google.com/s2/favicons?sz=64&domain=haekka.com",
  "hooksecurity": "https://www.google.com/s2/favicons?sz=64&domain=hooksecurity.com",
  "curricula": "https://www.google.com/s2/favicons?sz=64&domain=huntress.com",
  "kamae": "https://www.google.com/s2/favicons?sz=64&domain=kamae.com",
  "knowbe4": "https://www.google.com/s2/favicons?sz=64&domain=knowbe4.com",
  "moxso": "https://www.google.com/s2/favicons?sz=64&domain=moxso.com",
  "ninjio": "https://www.google.com/s2/favicons?sz=64&domain=ninjio.com",
  "outkept": "https://www.google.com/s2/favicons?sz=64&domain=outkept.com",
  "outthink": "https://www.google.com/s2/favicons?sz=64&domain=outthink.com",
  "ransomleak": "https://www.google.com/s2/favicons?sz=64&domain=ransomleak.com",
  "riot": "https://www.google.com/s2/favicons?sz=64&domain=riot.com",
  "safestack": "https://www.google.com/s2/favicons?sz=64&domain=safestack.com",
  "smartfense": "https://www.google.com/s2/favicons?sz=64&domain=smartfense.com",
  "sosafe": "https://www.google.com/s2/favicons?sz=64&domain=sosafe.com",
  "talentlms": "https://www.google.com/s2/favicons?sz=64&domain=talentlms.com",
  "vansec": "https://www.google.com/s2/favicons?sz=64&domain=vansec.com",
  "wizer": "https://www.google.com/s2/favicons?sz=64&domain=wizer.com",
  "aha": "https://www.google.com/s2/favicons?sz=64&domain=aha.com",
  "asana": "https://www.google.com/s2/favicons?sz=64&domain=asana.com",
  "basecamp": "https://www.google.com/s2/favicons?sz=64&domain=basecamp.com",
  "clickup": "https://www.google.com/s2/favicons?sz=64&domain=clickup.com",
  "dixa": "https://www.google.com/s2/favicons?sz=64&domain=dixa.com",
  "freshdesk": "https://www.google.com/s2/favicons?sz=64&domain=freshworks.com",
  "freshservice": "https://www.google.com/s2/favicons?sz=64&domain=freshworks.com",
  "front": "https://www.google.com/s2/favicons?sz=64&domain=front.com",
  "githubissues": "https://www.google.com/s2/favicons?sz=64&domain=github.com",
  "gitlabissues": "https://www.google.com/s2/favicons?sz=64&domain=gitlab.com",
  "gitlabissuesonprem": "https://www.google.com/s2/favicons?sz=64&domain=gitlab.com",
  "gladly": "https://www.google.com/s2/favicons?sz=64&domain=gladly.com",
  "gorgias": "https://www.google.com/s2/favicons?sz=64&domain=gorgias.com",
  "height": "https://www.google.com/s2/favicons?sz=64&domain=height.com",
  "helpscout": "https://www.google.com/s2/favicons?sz=64&domain=helpscout.com",
  "hive": "https://www.google.com/s2/favicons?sz=64&domain=hive.com",
  "humi": "https://www.google.com/s2/favicons?sz=64&domain=humi.com",
  "kustomer": "https://www.google.com/s2/favicons?sz=64&domain=kustomer.com",
  "linear": "https://www.google.com/s2/favicons?sz=64&domain=linear.app",
  "microsoftplanner": "https://www.google.com/s2/favicons?sz=64&domain=microsoft.com",
  "monday": "https://www.google.com/s2/favicons?sz=64&domain=monday.com",
  "pivotaltracker": "https://www.google.com/s2/favicons?sz=64&domain=pivotaltracker.com",
  "reamaze": "https://www.google.com/s2/favicons?sz=64&domain=reamaze.com",
  "shortcut": "https://www.google.com/s2/favicons?sz=64&domain=shortcut.com",
  "smartsheet": "https://www.google.com/s2/favicons?sz=64&domain=smartsheet.com",
  "spotdraft": "https://www.google.com/s2/favicons?sz=64&domain=spotdraft.com",
  "teamwork": "https://www.google.com/s2/favicons?sz=64&domain=teamwork.com",
  "trello": "https://www.google.com/s2/favicons?sz=64&domain=trello.com",
  "wrike": "https://www.google.com/s2/favicons?sz=64&domain=wrike.com",
  "youtrack": "https://www.google.com/s2/favicons?sz=64&domain=youtrack.com",
  "zohobugtracker": "https://www.google.com/s2/favicons?sz=64&domain=zohobugtracker.com",
  "zohodesk": "https://www.google.com/s2/favicons?sz=64&domain=zohodesk.com",
  "zohopeople": "https://www.google.com/s2/favicons?sz=64&domain=zohopeople.com",
  "awscodecommit": "https://www.google.com/s2/favicons?sz=64&domain=aws.amazon.com",
  "azuredevops": "https://www.google.com/s2/favicons?sz=64&domain=microsoft.com",
  "githubonprem": "https://www.google.com/s2/favicons?sz=64&domain=github.com",
  "gitlabonprem": "https://www.google.com/s2/favicons?sz=64&domain=gitlab.com",
  "13security": "https://www.google.com/s2/favicons?sz=64&domain=13security.com",
  "aftra": "https://www.google.com/s2/favicons?sz=64&domain=aftra.com",
  "aikido": "https://www.google.com/s2/favicons?sz=64&domain=aikido.com",
  "almanax": "https://www.google.com/s2/favicons?sz=64&domain=almanax.com",
  "astrasecurity": "https://www.google.com/s2/favicons?sz=64&domain=astrasecurity.com",
  "attackerview": "https://www.google.com/s2/favicons?sz=64&domain=attackerview.com",
  "awsguardduty": "https://www.google.com/s2/favicons?sz=64&domain=aws.amazon.com",
  "awsinspector": "https://www.google.com/s2/favicons?sz=64&domain=aws.amazon.com",
  "azuredefender": "https://www.google.com/s2/favicons?sz=64&domain=microsoft.com",
  "blacklocksecurity": "https://www.google.com/s2/favicons?sz=64&domain=blacklocksecurity.com",
  "botprise": "https://www.google.com/s2/favicons?sz=64&domain=botprise.com",
  "breachlock": "https://www.google.com/s2/favicons?sz=64&domain=breachlockinc.com",
  "capturethebug": "https://www.google.com/s2/favicons?sz=64&domain=capturethebug.com",
  "coana": "https://www.google.com/s2/favicons?sz=64&domain=coana.com",
  "cobalt": "https://www.google.com/s2/favicons?sz=64&domain=cobalt.io",
  "coguard": "https://www.google.com/s2/favicons?sz=64&domain=coguard.com",
  "contxt": "https://www.google.com/s2/favicons?sz=64&domain=contxt.com",
  "deepsource": "https://www.google.com/s2/favicons?sz=64&domain=deepsource.com",
  "edgebit": "https://www.google.com/s2/favicons?sz=64&domain=edgebit.com",
  "fencer": "https://www.google.com/s2/favicons?sz=64&domain=fencer.com",
  "gcpcontaineranalysis": "https://www.google.com/s2/favicons?sz=64&domain=cloud.google.com",
  "githubdependabot": "https://www.google.com/s2/favicons?sz=64&domain=github.com",
  "halosecurity": "https://www.google.com/s2/favicons?sz=64&domain=halosecurity.com",
  "heyhack": "https://www.google.com/s2/favicons?sz=64&domain=heyhack.com",
  "hostedscan": "https://www.google.com/s2/favicons?sz=64&domain=hostedscan.com",
  "intruder": "https://www.google.com/s2/favicons?sz=64&domain=intruder.com",
  "jitsecurity": "https://www.google.com/s2/favicons?sz=64&domain=jit.com",
  "lacework": "https://www.google.com/s2/favicons?sz=64&domain=lacework.com",
  "lumenovaai": "https://www.google.com/s2/favicons?sz=64&domain=lumenova.com",
  "defendergcchigh": "https://www.google.com/s2/favicons?sz=64&domain=microsoft.com",
  "defenderforcloud": "https://www.google.com/s2/favicons?sz=64&domain=microsoft.com",
  "orca": "https://www.google.com/s2/favicons?sz=64&domain=orca.com",
  "ostorlab": "https://www.google.com/s2/favicons?sz=64&domain=ostorlab.com",
  "panopticscans": "https://www.google.com/s2/favicons?sz=64&domain=panopticscans.com",
  "pentesttools": "https://www.google.com/s2/favicons?sz=64&domain=pentest-tools.com",
  "plerion": "https://www.google.com/s2/favicons?sz=64&domain=plerion.com",
  "prancer": "https://www.google.com/s2/favicons?sz=64&domain=prancer.com",
  "probely": "https://www.google.com/s2/favicons?sz=64&domain=probely.com",
  "qualys": "https://www.google.com/s2/favicons?sz=64&domain=qualys.com",
  "rapid7": "https://www.google.com/s2/favicons?sz=64&domain=rapid7.com",
  "sentinelone": "https://www.google.com/s2/favicons?sz=64&domain=sentinelone.com",
  "snyk": "https://www.google.com/s2/favicons?sz=64&domain=snyk.io",
  "socket": "https://www.google.com/s2/favicons?sz=64&domain=socket.com",
  "softwaresecured": "https://www.google.com/s2/favicons?sz=64&domain=softwaresecured.com",
  "soos": "https://www.google.com/s2/favicons?sz=64&domain=soos.com",
  "stackhawk": "https://www.google.com/s2/favicons?sz=64&domain=stackhawk.com",
  "strikesecurity": "https://www.google.com/s2/favicons?sz=64&domain=strikesecurity.com",
  "tenablevulnmgmt": "https://www.google.com/s2/favicons?sz=64&domain=tenable.com",
  "threatmate": "https://www.google.com/s2/favicons?sz=64&domain=threatmate.com",
  "trava": "https://www.google.com/s2/favicons?sz=64&domain=trava.com",
  "wiz": "https://www.google.com/s2/favicons?sz=64&domain=wiz.io",
  "wizforgoverment": "https://www.google.com/s2/favicons?sz=64&domain=wiz.io",

};

/* ── Logo component ────────────────────────────────────── */
function VendorIcon({ vendor, size = 'card' }) {
  const [imgBroken, setImgBroken] = useState(false);
  const logoUrl = LOGO_URLS[vendor.id];
  const px = size === 'panel' ? 38 : 36;

  if (logoUrl && !imgBroken) {
    return (
      <img
        src={logoUrl}
        alt={vendor.name}
        className="vc-logo-img"
        style={{ width: px, height: px, objectFit: 'contain' }}
        onError={() => setImgBroken(true)}
      />
    );
  }

  /* FA brands fallback */
  if (vendor.faFallback) {
    return (
      <i
        className={`${vendor.faFallback.includes(' ') ? vendor.faFallback : `fas ${vendor.faFallback}`}`}
        style={{ fontSize: px * 0.7, color: vendor.accent }}
      />
    );
  }

  /* Abbr / initial text fallback */
  return (
    <div
      className="vc-logo-text"
      style={{
        background: vendor.accent + '18',
        color: vendor.accent,
        fontSize: vendor.abbr && vendor.abbr.length > 2 ? px * 0.28 : px * 0.36,
        width: px,
        height: px,
      }}
    >
      {vendor.abbr || vendor.name.slice(0, 2).toUpperCase()}
    </div>
  );
}

/* ── Main component ──────────────────────────────────────
 * Optional props:
 *   orgContext = { id, name } — when set, this catalogue is being viewed by a CPA/CISO
 *                                on behalf of a client org. Wizard submissions route to
 *                                the partner-context endpoints, and the GitHub OAuth
 *                                state param carries the client org id so the callback
 *                                can attribute the install correctly.
 *   embedded   = boolean      — when true, drops the full-page paddings so the catalogue
 *                                renders cleanly inside a tab/panel.
 */
function VendorCards({ orgContext, embedded = false } = {}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  // The detail panel is a position:sticky right-column aside. When a card far down the long
  // catalogue is clicked, the panel mounts at the top of the layout — off-screen above the
  // viewport — so it looked like "nothing happened". Scroll it into view on selection.
  const panelRef = useRef(null);
  const [githubError, setGithubError] = useState('');
  const navigate = useNavigate();

  /* ── Vendor catalogue ──────────────────────────────────────── */
  const CATEGORIES = [
    {
      label: t('VendorCards.catCloudInfrastructure'),
      icon:  'fa-cloud',
      vendors: [
        {
          id: 'aws', name: 'AWS', tagline: 'Amazon Web Services',
          accent: '#FF9900', bg: 'rgba(255,153,0,0.06)',
          description: t('VendorCards.awsDescription'),
          assets: [
            { icon: 'fa-server',      label: t('VendorCards.awsAssetEc2') },
            { icon: 'fa-database',    label: t('VendorCards.awsAssetS3') },
            { icon: 'fa-bolt',        label: t('VendorCards.awsAssetLambda') },
            { icon: 'fa-th-list',     label: t('VendorCards.awsAssetRds') },
            { icon: 'fa-user-shield', label: t('VendorCards.awsAssetIam') },
          ],
          steps: [
            t('VendorCards.awsStep1'),
            t('VendorCards.awsStep2'),
            t('VendorCards.awsStep3'),
            t('VendorCards.awsStep4'),
          ],
        },
        {
          id: 'azure', name: 'Azure', tagline: 'Microsoft Azure',
          accent: '#0089D6', bg: 'rgba(0,137,214,0.06)',
          description: t('VendorCards.azureDescription'),
          assets: [
            { icon: 'fa-desktop',  label: t('VendorCards.azureAssetVm') },
            { icon: 'fa-archive',  label: t('VendorCards.azureAssetBlob') },
            { icon: 'fa-id-badge', label: t('VendorCards.azureAssetAdUsers') },
            { icon: 'fa-bolt',     label: t('VendorCards.azureAssetFunctions') },
            { icon: 'fa-sitemap',  label: t('VendorCards.azureAssetResourceGroups') },
          ],
          steps: [
            t('VendorCards.azureStep1'),
            t('VendorCards.azureStep2'),
            t('VendorCards.azureStep3'),
            t('VendorCards.azureStep4'),
          ],
        },
        {
          id: 'gcp', name: 'GCP', tagline: 'Google Cloud Platform',
          accent: '#4285F4', bg: 'rgba(66,133,244,0.06)',
          description: t('VendorCards.gcpDescription'),
          assets: [
            { icon: 'fa-server',      label: t('VendorCards.gcpAssetComputeVms') },
            { icon: 'fa-archive',     label: t('VendorCards.gcpAssetStorageBuckets') },
            { icon: 'fa-user-shield', label: t('VendorCards.gcpAssetIamPolicies') },
            { icon: 'fa-bolt',        label: t('VendorCards.gcpAssetCloudFunctions') },
          ],
          steps: [
            t('VendorCards.gcpStep1'),
            t('VendorCards.gcpStep2'),
            t('VendorCards.gcpStep3'),
            t('VendorCards.gcpStep4'),
          ],
        },
        {
          id: 'kubernetes', name: 'Kubernetes', tagline: t('VendorCards.kubernetesTagline'),
          accent: '#326CE5', bg: 'rgba(50,108,229,0.06)',
          description: t('VendorCards.kubernetesDescription'),
          assets: [
            { icon: 'fa-server',       label: t('VendorCards.kubernetesAssetNodes') },
            { icon: 'fa-cubes',        label: t('VendorCards.kubernetesAssetPods') },
            { icon: 'fa-network-wired',label: t('VendorCards.kubernetesAssetServices') },
            { icon: 'fa-layer-group',  label: t('VendorCards.kubernetesAssetNamespaces') },
          ],
          steps: [
            t('VendorCards.kubernetesStep1'),
            t('VendorCards.kubernetesStep2'),
            t('VendorCards.kubernetesStep3'),
          ],
        },
        {
          id: 'digitalocean', name: 'DigitalOcean', tagline: t('VendorCards.digitaloceanTagline'),
          accent: '#0080FF', bg: 'rgba(0,128,255,0.06)',
          description: t('VendorCards.digitaloceanDescription'),
          assets: [
            { icon: 'fa-server',  label: t('VendorCards.digitaloceanAssetDroplets') },
            { icon: 'fa-database',label: t('VendorCards.digitaloceanAssetDatabases') },
            { icon: 'fa-archive', label: t('VendorCards.digitaloceanAssetSpaces') },
            { icon: 'fa-network-wired', label: t('VendorCards.digitaloceanAssetVpcs') },
          ],
          steps: [
            t('VendorCards.digitaloceanStep1'),
            t('VendorCards.digitaloceanStep2'),
            t('VendorCards.digitaloceanStep3'),
          ],
        },
        {
          id: 'cloudflare', name: 'Cloudflare', tagline: 'Edge network, DNS & TLS',
          accent: '#F38020', bg: 'rgba(243,128,32,0.06)',
          faFallback: 'fa-cloud', abbr: 'CF',
          description: 'Discover the internet-facing zones fronted by Cloudflare and confirm each enforces end-to-end TLS.',
          assets: [
            { icon: 'fa-globe',         label: 'DNS zones' },
            { icon: 'fa-lock',          label: 'SSL/TLS mode' },
            { icon: 'fa-shield-alt',    label: 'WAF & proxy status' },
            { icon: 'fa-network-wired', label: 'Plan & routing' },
          ],
          steps: [
            'Create a scoped API token in Cloudflare → My Profile → API Tokens.',
            'Grant Zone: Read and SSL and Certificates: Read (all zones).',
            'Paste the token below to connect.',
          ],
        },
      ],
    },
    {
      label: t('VendorCards.catSourceControlDevops'),
      icon:  'fa-code-branch',
      vendors: [
        {
          id: 'github', name: 'GitHub', tagline: 'GitHub',
          accent: '#181717', bg: 'rgba(24,23,23,0.06)',
          description: t('VendorCards.githubDescription'),
          assets: [
            { icon: 'fa-code-branch', label: t('VendorCards.githubAssetRepos') },
            { icon: 'fa-code',        label: t('VendorCards.githubAssetPrs') },
            { icon: 'fa-users',       label: t('VendorCards.githubAssetContributors') },
            { icon: 'fa-shield-alt',  label: t('VendorCards.githubAssetSecurityAlerts') },
          ],
          steps: [
            t('VendorCards.githubStep1'),
            t('VendorCards.githubStep2'),
            t('VendorCards.githubStep3'),
          ],
          oauth: true,
        },
        {
          id: 'gitlab', name: 'GitLab', tagline: 'GitLab',
          accent: '#FC6D26', bg: 'rgba(252,109,38,0.06)',
          description: t('VendorCards.gitlabDescription'),
          assets: [
            { icon: 'fa-code-branch', label: t('VendorCards.gitlabAssetProjects') },
            { icon: 'fa-code',        label: t('VendorCards.gitlabAssetMrs') },
            { icon: 'fa-cogs',        label: t('VendorCards.gitlabAssetPipelines') },
            { icon: 'fa-shield-alt',  label: t('VendorCards.gitlabAssetScanFindings') },
          ],
          steps: [
            t('VendorCards.gitlabStep1'),
            t('VendorCards.gitlabStep2'),
            t('VendorCards.gitlabStep3'),
          ],
        },
        {
          id: 'bitbucket', name: 'Bitbucket', tagline: t('VendorCards.bitbucketTagline'),
          accent: '#0052CC', bg: 'rgba(0,82,204,0.06)',
          description: t('VendorCards.bitbucketDescription'),
          assets: [
            { icon: 'fa-code-branch', label: t('VendorCards.bitbucketAssetRepos') },
            { icon: 'fa-code',        label: t('VendorCards.bitbucketAssetPrs') },
            { icon: 'fa-cogs',        label: t('VendorCards.bitbucketAssetPipelines') },
            { icon: 'fa-users',       label: t('VendorCards.bitbucketAssetWorkspaceMembers') },
          ],
          steps: [
            t('VendorCards.bitbucketStep1'),
            t('VendorCards.bitbucketStep2'),
            t('VendorCards.bitbucketStep3'),
          ],
        },
      ],
    },
    {
      label: t('VendorCards.catIdentityAccess'),
      icon:  'fa-id-card',
      vendors: [
        {
          id: 'okta', name: 'Okta', tagline: t('VendorCards.oktaTagline'),
          accent: '#007DC1', bg: 'rgba(0,125,193,0.06)',
          description: t('VendorCards.oktaDescription'),
          assets: [
            { icon: 'fa-users',          label: t('VendorCards.oktaAssetUsersGroups') },
            { icon: 'fa-th',             label: t('VendorCards.oktaAssetApplications') },
            { icon: 'fa-shield-alt',     label: t('VendorCards.oktaAssetSsoMfaPolicies') },
            { icon: 'fa-history',        label: t('VendorCards.oktaAssetAuthLogs') },
          ],
          steps: [
            t('VendorCards.oktaStep1'),
            t('VendorCards.oktaStep2'),
            t('VendorCards.oktaStep3'),
          ],
        },
        {
          id: 'googleworkspace', name: 'Google Workspace', tagline: 'Google identity & directory',
          accent: '#4285F4', bg: 'rgba(66,133,244,0.06)',
          description: 'Discover Google Workspace users and groups and test 2-Step Verification.',
          assets: [
            { icon: 'fa-users',      label: 'Users & groups' },
            { icon: 'fa-shield-alt', label: '2-Step Verification' },
            { icon: 'fa-user-shield',label: 'Admin & status' },
          ],
          steps: [
            'In Google Cloud, create a service account + JSON key.',
            'In the Admin console, grant it domain-wide delegation with read-only Directory scopes.',
            'Paste the JSON key + an admin email here (read-only).',
          ],
        },
        {
          id: 'onelogin', name: 'OneLogin', tagline: 'Cloud identity & access',
          accent: '#1C1F2A', bg: 'rgba(28,31,42,0.06)',
          description: 'Discover OneLogin users and roles and test dormant-account and access controls.',
          assets: [
            { icon: 'fa-users',     label: 'Users & roles' },
            { icon: 'fa-user-clock',label: 'Sign-in activity' },
            { icon: 'fa-user-lock', label: 'Account status' },
          ],
          steps: [
            'In OneLogin, go to Developers → API Credentials → New Credential (read-only).',
            'Copy the Client ID and Client Secret.',
            'Enter your OneLogin subdomain + those credentials here (read-only).',
          ],
        },
        {
          id: 'jumpcloud', name: 'JumpCloud', tagline: 'Cloud directory & SSO',
          accent: '#17A2B8', bg: 'rgba(23,162,184,0.06)',
          description: 'Discover JumpCloud users and groups and test MFA and access controls.',
          assets: [
            { icon: 'fa-users',      label: 'Users & groups' },
            { icon: 'fa-shield-alt', label: 'MFA configuration' },
            { icon: 'fa-user-lock',  label: 'Account status' },
          ],
          steps: [
            'In the JumpCloud Admin Console, go to Settings → API.',
            'Generate an API key (bound to a read-only admin).',
            'Paste the API key here — read-only, no changes are made.',
          ],
        },
        {
          id: 'duo', name: 'Cisco Duo', tagline: 'MFA & device trust',
          accent: '#6BBE45', bg: 'rgba(107,190,69,0.06)',
          description: 'Discover Duo users and groups and test that no user is in 2FA bypass mode.',
          assets: [
            { icon: 'fa-users',      label: 'Users & groups' },
            { icon: 'fa-shield-alt', label: '2FA enrollment' },
            { icon: 'fa-user-lock',  label: 'Bypass status' },
          ],
          steps: [
            'In the Duo Admin Panel, go to Applications → Protect an Application → Admin API.',
            'Grant it "Grant read resource" permission and copy the integration key, secret key, and API hostname.',
            'Enter those three values here — read-only, no changes are made.',
          ],
        },
        {
          id: 'pingone', name: 'Ping Identity', tagline: 'PingOne cloud identity',
          accent: '#B01E28', bg: 'rgba(176,30,40,0.06)',
          description: 'Discover PingOne users and groups and test that users have MFA enabled.',
          assets: [
            { icon: 'fa-users',      label: 'Users & groups' },
            { icon: 'fa-shield-alt', label: 'MFA enablement' },
            { icon: 'fa-user-lock',  label: 'Account status' },
          ],
          steps: [
            'In the PingOne console, create a Worker application (read-only) and note its Client ID + Secret.',
            'Copy your Environment ID from the environment settings.',
            'Enter the environment ID, client ID/secret and region here — read-only, no changes are made.',
          ],
        },
        {
          id: 'auth0', name: 'Auth0', tagline: 'Auth0 by Okta identity',
          accent: '#EB5424', bg: 'rgba(235,84,36,0.06)',
          description: 'Discover Auth0 users and roles and test that users have an MFA factor enrolled.',
          assets: [
            { icon: 'fa-users',      label: 'Users & roles' },
            { icon: 'fa-shield-alt', label: 'MFA enrollment' },
            { icon: 'fa-user-lock',  label: 'Account status' },
          ],
          steps: [
            'In the Auth0 dashboard, go to Applications → create a Machine-to-Machine app authorized for the Management API.',
            'Grant it the read:users and read:roles scopes only, and copy the Client ID + Secret.',
            'Enter your Auth0 domain + those credentials here — read-only, no changes are made.',
          ],
        },
        {
          id: 'clerk', name: 'Clerk', tagline: 'User & organization management',
          accent: '#6C47FF', bg: 'rgba(108,71,255,0.06)',
          description: 'Discover Clerk users and organizations and test 2FA adoption, inactive accounts, and production-instance status.',
          assets: [
            { icon: 'fa-users',      label: 'Users & organizations' },
            { icon: 'fa-shield-alt', label: '2FA adoption' },
            { icon: 'fa-user-lock',  label: 'Account status' },
          ],
          steps: [
            'In the Clerk Dashboard, go to API Keys and copy the Secret Key (starts with sk_...).',
            'Paste the secret key here — read-only, no changes are made.',
          ],
        },
      ],
    },
    {
      label: t('VendorCards.catDeviceManagement', 'Device Management (MDM)'),
      icon:  'fa-laptop',
      vendors: [
        {
          id: 'jamf', name: 'Jamf Pro', tagline: 'Apple device management',
          accent: '#3B4B5B', bg: 'rgba(59,75,91,0.06)',
          description: 'Discover Jamf-managed computers and test that they have full-disk (FileVault) encryption.',
          assets: [
            { icon: 'fa-laptop',     label: 'Managed computers' },
            { icon: 'fa-lock',       label: 'Disk encryption' },
            { icon: 'fa-shield-alt', label: 'Security posture' },
          ],
          steps: [
            'In Jamf Pro, go to Settings → API Roles and Clients and create a read-only API Role + Client.',
            'Copy the Client ID and Client Secret, and note your Jamf URL (e.g. https://acme.jamfcloud.com).',
            'Enter those three values here — read-only, no changes are made.',
          ],
        },
        {
          id: 'intune', name: 'Microsoft Intune', tagline: 'Windows & mobile MDM',
          accent: '#0078D4', bg: 'rgba(0,120,212,0.06)',
          description: 'Discover Intune-managed devices and test that they report disk encryption enabled.',
          assets: [
            { icon: 'fa-laptop',     label: 'Managed devices' },
            { icon: 'fa-lock',       label: 'Disk encryption' },
            { icon: 'fa-clipboard-check', label: 'Compliance state' },
          ],
          steps: [
            'In Entra ID, register an app and grant it Graph DeviceManagementManagedDevices.Read.All (admin consent).',
            'Create a client secret and note the tenant ID, client ID and secret.',
            'Enter those three values here — read-only, no changes are made.',
          ],
        },
        {
          id: 'kandji', name: 'Kandji', tagline: 'Apple device management',
          accent: '#6A5CFF', bg: 'rgba(106,92,255,0.06)',
          description: 'Discover Kandji-managed devices and test that Macs have FileVault encryption.',
          assets: [
            { icon: 'fa-laptop',     label: 'Managed devices' },
            { icon: 'fa-lock',       label: 'FileVault encryption' },
            { icon: 'fa-shield-alt', label: 'Security posture' },
          ],
          steps: [
            'In Kandji, go to Settings → Access → API Token and create a read-only API token.',
            'Note your API URL (e.g. https://acme.api.kandji.io) and the token.',
            'Enter those two values here — read-only, no changes are made.',
          ],
        },
      ],
    },
    {
      label: t('VendorCards.catProjectIssueTracking'),
      icon:  'fa-tasks',
      vendors: [
        {
          id: 'jira', name: 'Jira', tagline: t('VendorCards.jiraTagline'),
          accent: '#0052CC', bg: 'rgba(0,82,204,0.06)',
          description: t('VendorCards.jiraDescription'),
          assets: [
            { icon: 'fa-project-diagram', label: t('VendorCards.jiraAssetProjects') },
            { icon: 'fa-tasks',           label: t('VendorCards.jiraAssetIssues') },
            { icon: 'fa-columns',         label: t('VendorCards.jiraAssetSprintBoards') },
            { icon: 'fa-users',           label: t('VendorCards.jiraAssetTeamMembers') },
          ],
          steps: [
            t('VendorCards.jiraStep1'),
            t('VendorCards.jiraStep2'),
            t('VendorCards.jiraStep3'),
          ],
        },
        {
          id: 'servicenow', name: 'ServiceNow', tagline: t('VendorCards.servicenowTagline'),
          accent: '#62D84E', bg: 'rgba(98,216,78,0.06)',
          faFallback: 'fa-tools', abbr: 'SN',
          description: t('VendorCards.servicenowDescription'),
          assets: [
            { icon: 'fa-server',      label: t('VendorCards.servicenowAssetCmdb') },
            { icon: 'fa-exclamation-circle', label: t('VendorCards.servicenowAssetIncidents') },
            { icon: 'fa-exchange-alt',label: t('VendorCards.servicenowAssetChangeRequests') },
            { icon: 'fa-list-alt',    label: t('VendorCards.servicenowAssetServiceCatalog') },
          ],
          steps: [
            t('VendorCards.servicenowStep1'),
            t('VendorCards.servicenowStep2'),
            t('VendorCards.servicenowStep3'),
          ],
        },
      ],
    },
    {
      label: t('VendorCards.catMonitoringObservability'),
      icon:  'fa-chart-line',
      vendors: [
        {
          id: 'datadog', name: 'Datadog', tagline: t('VendorCards.datadogTagline'),
          accent: '#632CA6', bg: 'rgba(99,44,166,0.06)',
          description: t('VendorCards.datadogDescription'),
          assets: [
            { icon: 'fa-server',     label: t('VendorCards.datadogAssetHosts') },
            { icon: 'fa-cogs',       label: t('VendorCards.datadogAssetServices') },
            { icon: 'fa-tachometer-alt', label: t('VendorCards.datadogAssetDashboards') },
            { icon: 'fa-shield-alt', label: t('VendorCards.datadogAssetSecuritySignals') },
          ],
          steps: [
            t('VendorCards.datadogStep1'),
            t('VendorCards.datadogStep2'),
          ],
        },
        {
          id: 'splunk', name: 'Splunk', tagline: t('VendorCards.splunkTagline'),
          accent: '#000000', bg: 'rgba(0,0,0,0.04)',
          description: t('VendorCards.splunkDescription'),
          assets: [
            { icon: 'fa-database',   label: t('VendorCards.splunkAssetIndexes') },
            { icon: 'fa-search',     label: t('VendorCards.splunkAssetSavedSearches') },
            { icon: 'fa-bell',       label: t('VendorCards.splunkAssetAlerts') },
            { icon: 'fa-users',      label: t('VendorCards.splunkAssetUsersRoles') },
          ],
          steps: [
            t('VendorCards.splunkStep1'),
            t('VendorCards.splunkStep2'),
          ],
        },
        {
          id: 'pagerduty', name: 'PagerDuty', tagline: t('VendorCards.pagerdutyTagline'),
          accent: '#06AC38', bg: 'rgba(6,172,56,0.06)',
          description: t('VendorCards.pagerdutyDescription'),
          assets: [
            { icon: 'fa-concierge-bell', label: t('VendorCards.pagerdutyAssetServices') },
            { icon: 'fa-user-clock',     label: t('VendorCards.pagerdutyAssetSchedules') },
            { icon: 'fa-sitemap',        label: t('VendorCards.pagerdutyAssetEscalation') },
            { icon: 'fa-exclamation-triangle', label: t('VendorCards.pagerdutyAssetIncidents') },
          ],
          steps: [
            t('VendorCards.pagerdutyStep1'),
            t('VendorCards.pagerdutyStep2'),
          ],
        },
        {
          id: 'newrelic', name: 'New Relic', tagline: t('VendorCards.newrelicTagline'),
          accent: '#008C99', bg: 'rgba(0,140,153,0.06)',
          description: t('VendorCards.newrelicDescription'),
          assets: [
            { icon: 'fa-desktop',         label: t('VendorCards.newrelicAssetApps') },
            { icon: 'fa-server',          label: t('VendorCards.newrelicAssetInfraHosts') },
            { icon: 'fa-tachometer-alt',  label: t('VendorCards.newrelicAssetDashboards') },
            { icon: 'fa-bell',            label: t('VendorCards.newrelicAssetAlertPolicies') },
          ],
          steps: [
            t('VendorCards.newrelicStep1'),
            t('VendorCards.newrelicStep2'),
          ],
        },
        {
          id: 'sentry', name: 'Sentry', tagline: 'Error & performance monitoring',
          accent: '#362D59', bg: 'rgba(54,45,89,0.06)',
          faFallback: 'fa-bug', abbr: 'SN',
          description: 'Discover the projects instrumented in Sentry and confirm the org enforces two-factor authentication.',
          assets: [
            { icon: 'fa-cubes',   label: 'Projects' },
            { icon: 'fa-bug',     label: 'Issue streams' },
            { icon: 'fa-users',   label: 'Team members' },
            { icon: 'fa-key',     label: '2FA enforcement' },
          ],
          steps: [
            'In Sentry go to Settings → Account → API → Auth Tokens → Create New Token.',
            'Grant org:read and project:read scopes, then copy the token.',
            'Enter the token and your organization slug below.',
          ],
        },
      ],
    },
    {
      label: t('VendorCards.catSecurity'),
      icon:  'fa-shield-alt',
      vendors: [
        {
          id: 'crowdstrike', name: 'CrowdStrike', tagline: t('VendorCards.crowdstrikeTagline'),
          accent: '#E00', bg: 'rgba(238,0,0,0.05)',
          faFallback: 'fa-shield-virus', abbr: 'CS',
          description: t('VendorCards.crowdstrikeDescription'),
          assets: [
            { icon: 'fa-laptop',     label: t('VendorCards.crowdstrikeAssetEndpoints') },
            { icon: 'fa-virus-slash',label: t('VendorCards.crowdstrikeAssetDetections') },
            { icon: 'fa-sliders-h',  label: t('VendorCards.crowdstrikeAssetDevicePolicies') },
            { icon: 'fa-users',      label: t('VendorCards.crowdstrikeAssetSensorGroups') },
          ],
          steps: [
            t('VendorCards.crowdstrikeStep1'),
            t('VendorCards.crowdstrikeStep2'),
            t('VendorCards.crowdstrikeStep3'),
          ],
        },
      ],
    },
    {
      label: t('VendorCards.catCollaboration'),
      icon:  'fa-comments',
      vendors: [
        {
          id: 'slack', name: 'Slack', tagline: t('VendorCards.slackTagline'),
          accent: '#4A154B', bg: 'rgba(74,21,75,0.06)',
          faFallback: 'fa-slack fab', abbr: 'SL',
          description: t('VendorCards.slackDescription'),
          assets: [
            { icon: 'fa-hashtag',  label: t('VendorCards.slackAssetChannels') },
            { icon: 'fa-users',    label: t('VendorCards.slackAssetUsersBots') },
            { icon: 'fa-th-large', label: t('VendorCards.slackAssetInstalledApps') },
            { icon: 'fa-lock',     label: t('VendorCards.slackAssetPrivateChannels') },
          ],
          steps: [
            t('VendorCards.slackStep1'),
            t('VendorCards.slackStep2'),
            t('VendorCards.slackStep3'),
          ],
        },
        {
          id: 'microsoft', name: 'Microsoft 365', tagline: 'Microsoft 365',
          accent: '#D83B01', bg: 'rgba(216,59,1,0.06)',
          description: t('VendorCards.microsoftDescription'),
          assets: [
            { icon: 'fa-users',    label: t('VendorCards.microsoftAssetUsersGroups') },
            { icon: 'fa-comments', label: t('VendorCards.microsoftAssetTeams') },
            { icon: 'fa-file-alt', label: t('VendorCards.microsoftAssetSharepoint') },
            { icon: 'fa-id-card',  label: t('VendorCards.microsoftAssetEntraId') },
          ],
          steps: [
            t('VendorCards.microsoftStep1'),
            t('VendorCards.microsoftStep2'),
            t('VendorCards.microsoftStep3'),
          ],
        },
      ],
    },
    {
      label: t('VendorCards.catDataAnalytics'),
      icon:  'fa-database',
      vendors: [
        {
          id: 'snowflake', name: 'Snowflake', tagline: t('VendorCards.snowflakeTagline'),
          accent: '#29B5E8', bg: 'rgba(41,181,232,0.06)',
          description: t('VendorCards.snowflakeDescription'),
          assets: [
            { icon: 'fa-database',   label: t('VendorCards.snowflakeAssetDatabases') },
            { icon: 'fa-table',      label: t('VendorCards.snowflakeAssetTables') },
            { icon: 'fa-server',     label: t('VendorCards.snowflakeAssetWarehouses') },
            { icon: 'fa-user-shield',label: t('VendorCards.snowflakeAssetUsersRoles') },
          ],
          steps: [
            t('VendorCards.snowflakeStep1'),
            t('VendorCards.snowflakeStep2'),
            t('VendorCards.snowflakeStep3'),
          ],
        },
      ],
    },
    {
      label: "Audit Management",
      icon:  "fa-clipboard-check",
      vendors: [
        {
          id: "ascend", name: "A-SCEND", tagline: "Audit management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-clipboard-check", abbr: "AS",
          description: "Connect A-SCEND read-only to evidence the audit management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in A-SCEND.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "assurancelab", name: "Assurance Lab", tagline: "Audit management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-clipboard-check", abbr: "AS",
          description: "Connect Assurance Lab read-only to evidence the audit management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Assurance Lab.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "audora", name: "Audora", tagline: "Audit management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-clipboard-check", abbr: "AU",
          description: "Connect Audora read-only to evidence the audit management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Audora.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "fieldguide", name: "Fieldguide", tagline: "Audit management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-clipboard-check", abbr: "FI",
          description: "Connect Fieldguide read-only to evidence the audit management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Fieldguide.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "trenta", name: "Trenta", tagline: "Audit management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-clipboard-check", abbr: "TR",
          description: "Connect Trenta read-only to evidence the audit management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Trenta.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "Background Checks",
      icon:  "fa-user-check",
      vendors: [
        {
          id: "certn", name: "Certn", tagline: "Background checks integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-user-check", abbr: "CE",
          description: "Connect Certn read-only to evidence the background checks control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Certn.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "checkr", name: "Checkr", tagline: "Background checks integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-user-check", abbr: "CH",
          description: "Connect Checkr read-only to evidence the background checks control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Checkr.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "hirepass", name: "HirePass", tagline: "Background checks integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-user-check", abbr: "HI",
          description: "Connect HirePass read-only to evidence the background checks control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in HirePass.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "veremark", name: "Veremark", tagline: "Background checks integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-user-check", abbr: "VE",
          description: "Connect Veremark read-only to evidence the background checks control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Veremark.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "vetty", name: "Vetty", tagline: "Background checks integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-user-check", abbr: "VE",
          description: "Connect Vetty read-only to evidence the background checks control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Vetty.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "zinc", name: "Zinc", tagline: "Background checks integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-user-check", abbr: "ZI",
          description: "Connect Zinc read-only to evidence the background checks control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Zinc.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "CRM",
      icon:  "fa-address-book",
      vendors: [
        {
          id: "capsule", name: "Capsule", tagline: "CRM integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-address-book", abbr: "CA",
          description: "Connect Capsule read-only to evidence the crm control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Capsule.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "close", name: "Close", tagline: "CRM integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-address-book", abbr: "CL",
          description: "Connect Close read-only to evidence the crm control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Close.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "copper", name: "Copper", tagline: "CRM integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-address-book", abbr: "CO",
          description: "Connect Copper read-only to evidence the crm control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Copper.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "hubspot", name: "HubSpot", tagline: "CRM integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-address-book", abbr: "HU",
          description: "Connect HubSpot read-only to evidence the crm control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in HubSpot.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "insightly", name: "Insightly", tagline: "CRM integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-address-book", abbr: "IN",
          description: "Connect Insightly read-only to evidence the crm control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Insightly.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "salesforce", name: "Salesforce", tagline: "CRM integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-address-book", abbr: "SA",
          description: "Connect Salesforce read-only to evidence the crm control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Salesforce.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "zendesk", name: "Zendesk", tagline: "CRM integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-address-book", abbr: "ZE",
          description: "Connect Zendesk read-only to evidence the crm control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Zendesk.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "More Cloud Infrastructure",
      icon:  "fa-cloud",
      vendors: [
        {
          id: "amazonecr", name: "Amazon ECR", tagline: "Cloud infrastructure integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-cloud", abbr: "AM",
          description: "Connect Amazon ECR read-only to evidence the cloud infrastructure control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Amazon ECR.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "awsgovcloud", name: "AWS GovCloud", tagline: "Cloud infrastructure integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-cloud", abbr: "AW",
          description: "Connect AWS GovCloud read-only to evidence the cloud infrastructure control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in AWS GovCloud.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "digitaloceanspaces", name: "DigitalOcean Spaces", tagline: "Cloud infrastructure integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-cloud", abbr: "DI",
          description: "Connect DigitalOcean Spaces read-only to evidence the cloud infrastructure control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in DigitalOcean Spaces.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "gcpartifact", name: "GCP Artifact Registry", tagline: "Cloud infrastructure integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-cloud", abbr: "GC",
          description: "Connect GCP Artifact Registry read-only to evidence the cloud infrastructure control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in GCP Artifact Registry.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "grafanacloud", name: "Grafana Cloud", tagline: "Cloud infrastructure integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-cloud", abbr: "GR",
          description: "Connect Grafana Cloud read-only to evidence the cloud infrastructure control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Grafana Cloud.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "heroku", name: "Heroku", tagline: "Cloud infrastructure integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-cloud", abbr: "HE",
          description: "Connect Heroku read-only to evidence the cloud infrastructure control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Heroku.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "netlify", name: "Netlify", tagline: "Cloud infrastructure integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-cloud", abbr: "NE",
          description: "Connect Netlify read-only to evidence the cloud infrastructure control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Netlify.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "oci", name: "Oracle Cloud Infrastructure (OCI)", tagline: "Cloud infrastructure integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-cloud", abbr: "OR",
          description: "Connect Oracle Cloud Infrastructure (OCI) read-only to evidence the cloud infrastructure control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Oracle Cloud Infrastructure (OCI).", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ovhcloud", name: "OVHcloud", tagline: "Cloud infrastructure integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-cloud", abbr: "OV",
          description: "Connect OVHcloud read-only to evidence the cloud infrastructure control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in OVHcloud.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "railway", name: "Railway", tagline: "Cloud infrastructure integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-cloud", abbr: "RA",
          description: "Connect Railway read-only to evidence the cloud infrastructure control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Railway.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "render", name: "Render", tagline: "Cloud infrastructure integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-cloud", abbr: "RE",
          description: "Connect Render read-only to evidence the cloud infrastructure control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Render.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "scaleway", name: "Scaleway", tagline: "Cloud infrastructure integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-cloud", abbr: "SC",
          description: "Connect Scaleway read-only to evidence the cloud infrastructure control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Scaleway.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "vercel", name: "Vercel", tagline: "Deployments & edge hosting",
          accent: '#000000', bg: 'rgba(0,0,0,0.06)', faFallback: "fa-cloud", abbr: "VE",
          description: "Discover Vercel projects and confirm deployment protection, team SSO enforcement, and environment-variable handling.",
          assets: [
            { icon: 'fa-server', label: 'Projects' },
            { icon: 'fa-shield-alt', label: 'Deployment protection' },
            { icon: 'fa-key', label: 'Environment variables' },
          ],
          steps: [
            'In Vercel, go to Account Settings → Tokens → Create Token (a read-only scope is enough).',
            'If the projects live under a Team, also copy the Team ID from Team Settings → General.',
            'Paste the token (and Team ID, if applicable) below to connect.',
          ],
        },
      ],
    },
    {
      label: "More Communication",
      icon:  "fa-comments",
      vendors: [
        {
          id: 'postmark', name: 'Postmark', tagline: 'Transactional email delivery',
          accent: '#FFDE00', bg: 'rgba(255,222,0,0.12)', faFallback: 'fa-envelope', abbr: 'PM',
          description: 'Discover Postmark sending domains and test DKIM signing, custom Return-Path verification, and SPF status.',
          assets: [
            { icon: 'fa-globe',       label: 'Sending domains' },
            { icon: 'fa-shield-alt',  label: 'DKIM signing' },
            { icon: 'fa-envelope',    label: 'Return-Path / SPF' },
          ],
          steps: [
            'In the Postmark account, go to Account API Tokens (account-level, not a server/sending token -- the Domains API this connector reads requires it).',
            'Paste the Account API token here — read-only, no changes are made.',
          ],
        },
        {
          id: "intercom", name: "Intercom", tagline: "Communication integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-comments", abbr: "IN",
          description: "Connect Intercom read-only to evidence the communication control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Intercom.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "zoom", name: "Zoom", tagline: "Communication integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-comments", abbr: "ZO",
          description: "Connect Zoom read-only to evidence the communication control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Zoom.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "More Data & Analytics",
      icon:  "fa-database",
      vendors: [
        {
          id: "databricks", name: "Databricks", tagline: "Data warehouse integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-database", abbr: "DA",
          description: "Connect Databricks read-only to evidence the data warehouse control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Databricks.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "mongoatlas", name: "MongoDB Atlas", tagline: "Datastore integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-database", abbr: "MO",
          description: "Connect MongoDB Atlas read-only to evidence the datastore control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in MongoDB Atlas.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "Document Management",
      icon:  "fa-file-alt",
      vendors: [
        {
          id: "box", name: "Box", tagline: "Document management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-file-alt", abbr: "BO",
          description: "Connect Box read-only to evidence the document management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Box.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "dropbox", name: "Dropbox", tagline: "Document management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-file-alt", abbr: "DR",
          description: "Connect Dropbox read-only to evidence the document management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Dropbox.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "googledrive", name: "Google Drive", tagline: "Document management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-file-alt", abbr: "GO",
          description: "Connect Google Drive read-only to evidence the document management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Google Drive.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "guru", name: "Guru", tagline: "Document management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-file-alt", abbr: "GU",
          description: "Connect Guru read-only to evidence the document management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Guru.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "microsoftsharepoint", name: "Microsoft Sharepoint", tagline: "Document management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-file-alt", abbr: "MI",
          description: "Connect Microsoft Sharepoint read-only to evidence the document management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Microsoft Sharepoint.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "notion", name: "Notion", tagline: "Document management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-file-alt", abbr: "NO",
          description: "Connect Notion read-only to evidence the document management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Notion.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "pima", name: "Pima", tagline: "Document management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-file-alt", abbr: "PI",
          description: "Connect Pima read-only to evidence the document management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Pima.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "More Endpoint Security",
      icon:  "fa-shield-alt",
      vendors: [
        {
          id: "agency", name: "Agency", tagline: "Endpoint security integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-shield-alt", abbr: "AG",
          description: "Connect Agency read-only to evidence the endpoint security control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Agency.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "automox", name: "Automox", tagline: "Endpoint security integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-shield-alt", abbr: "AU",
          description: "Connect Automox read-only to evidence the endpoint security control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Automox.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "hexnode", name: "Hexnode", tagline: "Endpoint security integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-shield-alt", abbr: "HE",
          description: "Connect Hexnode read-only to evidence the endpoint security control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Hexnode.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "kitecyber", name: "Kitecyber", tagline: "Endpoint security integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-shield-alt", abbr: "KI",
          description: "Connect Kitecyber read-only to evidence the endpoint security control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Kitecyber.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "HRIS & Personnel",
      icon:  "fa-users",
      vendors: [
        {
          id: "7shifts", name: "7shifts", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "7S",
          description: "Connect 7shifts read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in 7shifts.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "adpworkforce", name: "ADP Workforce Now", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "AD",
          description: "Connect ADP Workforce Now read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in ADP Workforce Now.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "alexishr", name: "AlexisHR", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "AL",
          description: "Connect AlexisHR read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in AlexisHR.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "alterapayroll", name: "Altera Payroll", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "AL",
          description: "Connect Altera Payroll read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Altera Payroll.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "bamboohr", name: "BambooHR", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "BA",
          description: "Connect BambooHR read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in BambooHR.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "bob", name: "Bob", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "BO",
          description: "Connect Bob read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Bob.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "breathe", name: "Breathe", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "BR",
          description: "Connect Breathe read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Breathe.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "breezyhr", name: "Breezy HR", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "BR",
          description: "Connect Breezy HR read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Breezy HR.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "breezyhrx", name: "BreezyHR", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "BR",
          description: "Connect BreezyHR read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in BreezyHR.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "central", name: "Central", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "CE",
          description: "Connect Central read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Central.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "dayforce", name: "Ceridian Dayforce", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "CE",
          description: "Connect Ceridian Dayforce read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Ceridian Dayforce.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "charliehr", name: "CharlieHR", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "CH",
          description: "Connect CharlieHR read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in CharlieHR.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "clayhr", name: "ClayHR", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "CL",
          description: "Connect ClayHR read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in ClayHR.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "cyberark", name: "CyberArk", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "CY",
          description: "Connect CyberArk read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in CyberArk.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "deel", name: "Deel", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "DE",
          description: "Connect Deel read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Deel.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "employmenthero", name: "Employment Hero", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "EM",
          description: "Connect Employment Hero read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Employment Hero.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "factorial", name: "Factorial", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "FA",
          description: "Connect Factorial read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Factorial.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "freshteam", name: "Freshteam", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "FR",
          description: "Connect Freshteam read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Freshteam.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "gusto", name: "Gusto", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "GU",
          description: "Connect Gusto read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Gusto.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "hrcloud", name: "HR Cloud", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "HR",
          description: "Connect HR Cloud read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in HR Cloud.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "hrpartner", name: "HR Partner", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "HR",
          description: "Connect HR Partner read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in HR Partner.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "humaans", name: "Humaans", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "HU",
          description: "Connect Humaans read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Humaans.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "insperity", name: "Insperity", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "IN",
          description: "Connect Insperity read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Insperity.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "intellihr", name: "IntelliHR", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "IN",
          description: "Connect IntelliHR read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in IntelliHR.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "isolved", name: "ISolved", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "IS",
          description: "Connect ISolved read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in ISolved.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "justworks", name: "Justworks", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "JU",
          description: "Connect Justworks read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Justworks.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "kallidus", name: "Kallidus (Sapling)", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "KA",
          description: "Connect Kallidus (Sapling) read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Kallidus (Sapling).", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "keka", name: "Keka", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "KE",
          description: "Connect Keka read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Keka.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "kenjo", name: "Kenjo", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "KE",
          description: "Connect Kenjo read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Kenjo.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "lano", name: "Lano", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "LA",
          description: "Connect Lano read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Lano.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "lucca", name: "LuccaHR", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "LU",
          description: "Connect LuccaHR read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in LuccaHR.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "namely", name: "Namely", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "NA",
          description: "Connect Namely read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Namely.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "netsuite", name: "Netsuite", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "NE",
          description: "Connect Netsuite read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Netsuite.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "nmbrs", name: "Nmbrs", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "NM",
          description: "Connect Nmbrs read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Nmbrs.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "officient", name: "Officient", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "OF",
          description: "Connect Officient read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Officient.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "oraclecloudhcm", name: "Oracle Cloud HCM", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "OR",
          description: "Connect Oracle Cloud HCM read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Oracle Cloud HCM.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "oyster", name: "Oyster", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "OY",
          description: "Connect Oyster read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Oyster.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "paycaptain", name: "Paycaptain", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "PA",
          description: "Connect Paycaptain read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Paycaptain.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "paychexflex", name: "Paychex Flex", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "PA",
          description: "Connect Paychex Flex read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Paychex Flex.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "paycor", name: "Paycor", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "PA",
          description: "Connect Paycor read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Paycor.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "payfit95f52", name: "Payfit", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "PA",
          description: "Connect Payfit read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Payfit.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "paylocity", name: "Paylocity", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "PA",
          description: "Connect Paylocity read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Paylocity.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "payworks", name: "Payworks", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "PA",
          description: "Connect Payworks read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Payworks.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "peoplehr", name: "PeopleHR", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "PE",
          description: "Connect PeopleHR read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in PeopleHR.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "personio", name: "Personio", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "PE",
          description: "Connect Personio read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Personio.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "proliant", name: "Proliant", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "PR",
          description: "Connect Proliant read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Proliant.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "quickbooks", name: "QuickBooks", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "QU",
          description: "Connect QuickBooks read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in QuickBooks.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "remote", name: "Remote", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "RE",
          description: "Connect Remote read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Remote.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "rippling", name: "Rippling", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "RI",
          description: "Connect Rippling read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Rippling.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "sage", name: "Sage", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "SA",
          description: "Connect Sage read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Sage.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "sagehr", name: "Sage HR", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "SA",
          description: "Connect Sage HR read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Sage HR.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "sapsuccessfactors", name: "SAP Successfactors", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "SA",
          description: "Connect SAP Successfactors read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in SAP Successfactors.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "sesame", name: "Sesame", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "SE",
          description: "Connect Sesame read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Sesame.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "squarepayrollf5639", name: "Square Payroll", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "SQ",
          description: "Connect Square Payroll read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Square Payroll.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "torii", name: "Torii", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "TO",
          description: "Connect Torii read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Torii.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "trinet", name: "TriNet", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "TR",
          description: "Connect TriNet read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in TriNet.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ukgdimensions", name: "UKG Dimensions", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "UK",
          description: "Connect UKG Dimensions read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in UKG Dimensions.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ukgpro", name: "UKG Pro", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "UK",
          description: "Connect UKG Pro read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in UKG Pro.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ukgready", name: "UKG Ready", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "UK",
          description: "Connect UKG Ready read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in UKG Ready.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "workable", name: "Workable", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "WO",
          description: "Connect Workable read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Workable.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "workday", name: "Workday", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "WO",
          description: "Connect Workday read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Workday.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "zenefits", name: "Zenefits", tagline: "HR / personnel integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-users", abbr: "ZE",
          description: "Connect Zenefits read-only to evidence the hr / personnel control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Zenefits.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "More Identity & Access",
      icon:  "fa-id-badge",
      vendors: [
        {
          id: "azuread", name: "Azure Active Directory", tagline: "Identity provider integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-id-badge", abbr: "AZ",
          description: "Connect Azure Active Directory read-only to evidence the identity provider control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Azure Active Directory.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "office365", name: "Entra (Office 365)", tagline: "Identity provider integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-id-badge", abbr: "EN",
          description: "Connect Entra (Office 365) read-only to evidence the identity provider control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Entra (Office 365).", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "entragcchigh", name: "Microsoft Entra (GCC High)", tagline: "Identity provider integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-id-badge", abbr: "MI",
          description: "Connect Microsoft Entra (GCC High) read-only to evidence the identity provider control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Microsoft Entra (GCC High).", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "sclaeai", name: "ScaleAI", tagline: "Identity provider integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-id-badge", abbr: "SC",
          description: "Connect ScaleAI read-only to evidence the identity provider control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in ScaleAI.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "More Incident Management",
      icon:  "fa-bell",
      vendors: [
        {
          id: "breachrx", name: "BreachRX", tagline: "Incident management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bell", abbr: "BR",
          description: "Connect BreachRX read-only to evidence the incident management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in BreachRX.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "incident", name: "Incident.io", tagline: "Incident management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bell", abbr: "IN",
          description: "Connect Incident.io read-only to evidence the incident management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Incident.io.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "More Device Management",
      icon:  "fa-laptop",
      vendors: [
        {
          id: "addigy", name: "Addigy", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "AD",
          description: "Connect Addigy read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Addigy.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "datto", name: "Datto", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "DA",
          description: "Connect Datto read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Datto.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "edamametechnologies", name: "EDAMAME Technologies", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "ED",
          description: "Connect EDAMAME Technologies read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in EDAMAME Technologies.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "fleet", name: "Fleet", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "FL",
          description: "Connect Fleet read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Fleet.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "microsoftintune", name: "Intune (Microsoft Endpoint Manager)", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "IN",
          description: "Connect Intune (Microsoft Endpoint Manager) read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Intune (Microsoft Endpoint Manager).", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "iru", name: "Iru", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "IR",
          description: "Connect Iru read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Iru.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "kolide", name: "Kolide, by 1Password", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "KO",
          description: "Connect Kolide, by 1Password read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Kolide, by 1Password.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "manageengine", name: "Manage Engine", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "MA",
          description: "Connect Manage Engine read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Manage Engine.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "microsoftendpointmanager", name: "Microsoft Endpoint Manager", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "MI",
          description: "Connect Microsoft Endpoint Manager read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Microsoft Endpoint Manager.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "intunegcchigh", name: "Microsoft Intune (GCC High)", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "MI",
          description: "Connect Microsoft Intune (GCC High) read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Microsoft Intune (GCC High).", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "miradore", name: "Miradore", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "MI",
          description: "Connect Miradore read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Miradore.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ninjaone", name: "NinjaOne", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "NI",
          description: "Connect NinjaOne read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in NinjaOne.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "omnissa", name: "Omnissa Workspace One", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "OM",
          description: "Connect Omnissa Workspace One read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Omnissa Workspace One.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "primo", name: "Primo", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "PR",
          description: "Connect Primo read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Primo.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "simplemdm", name: "SimpleMDM", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "SI",
          description: "Connect SimpleMDM read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in SimpleMDM.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "swif", name: "Swif", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "SW",
          description: "Connect Swif read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Swif.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "trio", name: "Trio", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "TR",
          description: "Connect Trio read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Trio.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "vmwareworkspaceone", name: "VMWare Workspace One", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "VM",
          description: "Connect VMWare Workspace One read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in VMWare Workspace One.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "xcitium", name: "Xcitium", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "XC",
          description: "Connect Xcitium read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Xcitium.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "xfa", name: "XFA", tagline: "Device management integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-laptop", abbr: "XF",
          description: "Connect XFA read-only to evidence the device management control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in XFA.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "More Monitoring",
      icon:  "fa-chart-line",
      vendors: [
        {
          id: "cobrickobserve", name: "co.brick observe", tagline: "Observability integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-chart-line", abbr: "CO",
          description: "Connect co.brick observe read-only to evidence the observability control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in co.brick observe.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "sumologic", name: "Sumo Logic", tagline: "Observability integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-chart-line", abbr: "SU",
          description: "Connect Sumo Logic read-only to evidence the observability control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Sumo Logic.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "Other Integrations",
      icon:  "fa-plug",
      vendors: [
        {
          id: "1password", name: "1Password Device Trust", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "1P",
          description: "Connect 1Password Device Trust read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in 1Password Device Trust.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "4me", name: "4me", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "4M",
          description: "Connect 4me read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in 4me.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "8x8", name: "8x8", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "8X",
          description: "Connect 8x8 read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in 8x8.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "abv", name: "Abv", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AB",
          description: "Connect Abv read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Abv.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "accessowl", name: "AccessOwl", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AC",
          description: "Connect AccessOwl read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in AccessOwl.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "acunetix360", name: "Acunetix360", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AC",
          description: "Connect Acunetix360 read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Acunetix360.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "adobelearning", name: "Adobe Learning Manager", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AD",
          description: "Connect Adobe Learning Manager read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Adobe Learning Manager.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "airtable", name: "Airtable", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AI",
          description: "Connect Airtable read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Airtable.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "akamai", name: "Akamai", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AK",
          description: "Connect Akamai read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Akamai.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "alertmedia", name: "Alert Media", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AL",
          description: "Connect Alert Media read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Alert Media.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "amplitude", name: "Amplitude", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AM",
          description: "Connect Amplitude read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Amplitude.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "anthropic", name: "Anthropic", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AN",
          description: "Connect Anthropic read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Anthropic.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "apollo", name: "Apollo", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AP",
          description: "Connect Apollo read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Apollo.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "appaegis76b4c", name: "Appaegis", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AP",
          description: "Connect Appaegis read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Appaegis.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "applebusinessmanager", name: "Apple Business Manager", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AP",
          description: "Connect Apple Business Manager read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Apple Business Manager.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "appranix", name: "Appranix", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AP",
          description: "Connect Appranix read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Appranix.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ashby", name: "Ashby", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AS",
          description: "Connect Ashby read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Ashby.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "assetpanda", name: "Asset Panda", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AS",
          description: "Connect Asset Panda read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Asset Panda.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "axisatmos", name: "Atmos", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AT",
          description: "Connect Atmos read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Atmos.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "autodesk", name: "Autodesk", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AU",
          description: "Connect Autodesk read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Autodesk.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "azuresynapse", name: "Azure Synapse Analytics", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "AZ",
          description: "Connect Azure Synapse Analytics read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Azure Synapse Analytics.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "bis", name: "BIS", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "BI",
          description: "Connect BIS read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in BIS.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "bitwarden", name: "Bitwarden", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "BI",
          description: "Connect Bitwarden read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Bitwarden.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "blink", name: "Blink", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "BL",
          description: "Connect Blink read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Blink.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "blinq", name: "Blinq", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "BL",
          description: "Connect Blinq read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Blinq.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "blogin", name: "Blogin", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "BL",
          description: "Connect Blogin read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Blogin.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "bluejeans", name: "Bluejeans", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "BL",
          description: "Connect Bluejeans read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Bluejeans.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "bocada", name: "Bocada", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "BO",
          description: "Connect Bocada read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Bocada.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "brex", name: "Brex", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "BR",
          description: "Connect Brex read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Brex.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "britive", name: "Britive", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "BR",
          description: "Connect Britive read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Britive.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "browserstack", name: "BrowserStack", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "BR",
          description: "Connect BrowserStack read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in BrowserStack.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "calendly", name: "Calendly", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CA",
          description: "Connect Calendly read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Calendly.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "cats", name: "CATS", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CA",
          description: "Connect CATS read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in CATS.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "charthop", name: "ChartHop", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CH",
          description: "Connect ChartHop read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in ChartHop.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "checkproof", name: "CheckProof", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CH",
          description: "Connect CheckProof read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in CheckProof.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "cinode", name: "Cinode", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CI",
          description: "Connect Cinode read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Cinode.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "cleanmailf734a", name: "Cleanmail", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CL",
          description: "Connect Cleanmail read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Cleanmail.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "clebex", name: "Clebex", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CL",
          description: "Connect Clebex read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Clebex.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "clockworkrecruiter", name: "Clockwork Recruiting", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CL",
          description: "Connect Clockwork Recruiting read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Clockwork Recruiting.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "cloudback", name: "Cloudback", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CL",
          description: "Connect Cloudback read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Cloudback.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "coda", name: "Coda", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CO",
          description: "Connect Coda read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Coda.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "comeet", name: "Comeet", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CO",
          description: "Connect Comeet read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Comeet.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "commonpaper", name: "Common Paper", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CO",
          description: "Connect Common Paper read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Common Paper.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "confluence", name: "Confluence", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CO",
          description: "Connect Confluence read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Confluence.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "contentful", name: "Contentful", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CO",
          description: "Connect Contentful read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Contentful.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "continuitystrength", name: "Continuity Strength", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CO",
          description: "Connect Continuity Strength read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Continuity Strength.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "cside", name: "cside", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CS",
          description: "Connect cside read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in cside.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "cybsafe", name: "CybSafe", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "CY",
          description: "Connect CybSafe read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in CybSafe.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "dashlane", name: "Dashlane", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "DA",
          description: "Connect Dashlane read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Dashlane.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "dbtlab", name: "dbt Labs", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "DB",
          description: "Connect dbt Labs read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in dbt Labs.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "dockerhub", name: "Dockerhub", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "DO",
          description: "Connect Dockerhub read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Dockerhub.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "documo", name: "Documo", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "DO",
          description: "Connect Documo read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Documo.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "docusign", name: "Docusign", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "DO",
          description: "Connect Docusign read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Docusign.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "doppler", name: "Doppler", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "DO",
          description: "Connect Doppler read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Doppler.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "drift", name: "Drift", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "DR",
          description: "Connect Drift read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Drift.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "dynatrace", name: "Dynatrace", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "DY",
          description: "Connect Dynatrace read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Dynatrace.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "elastic", name: "Elastic", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "EL",
          description: "Connect Elastic read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Elastic.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "eletive", name: "Eletive", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "EL",
          description: "Connect Eletive read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Eletive.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "elium", name: "elium", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "EL",
          description: "Connect elium read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in elium.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "envkey", name: "EnvKey", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "EN",
          description: "Connect EnvKey read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in EnvKey.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "envoy", name: "Envoy", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "EN",
          description: "Connect Envoy read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Envoy.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "evervault", name: "Evervault", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "EV",
          description: "Connect Evervault read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Evervault.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "exium", name: "Exium", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "EX",
          description: "Connect Exium read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Exium.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "faceup", name: "FaceUp", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "FA",
          description: "Connect FaceUp read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in FaceUp.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "figma", name: "Figma", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "FI",
          description: "Connect Figma read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Figma.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "fivetran", name: "Fivetran", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "FI",
          description: "Connect Fivetran read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Fivetran.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "flock", name: "Flock", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "FL",
          description: "Connect Flock read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Flock.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "foodee", name: "Foodee", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "FO",
          description: "Connect Foodee read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Foodee.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "fourfour", name: "Four/Four", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "FO",
          description: "Connect Four/Four read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Four/Four.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "gdprlocal", name: "GDPRLocal", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "GD",
          description: "Connect GDPRLocal read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in GDPRLocal.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ghostable", name: "Ghostable", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "GH",
          description: "Connect Ghostable read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Ghostable.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "gong", name: "Gong", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "GO",
          description: "Connect Gong read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Gong.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "grammarly", name: "Grammarly", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "GR",
          description: "Connect Grammarly read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Grammarly.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "greenhouse", name: "Greenhouse", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "GR",
          description: "Connect Greenhouse read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Greenhouse.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "griffo", name: "Griffo", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "GR",
          description: "Connect Griffo read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Griffo.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "h5mag", name: "H5mag", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "H5",
          description: "Connect H5mag read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in H5mag.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "harness", name: "Harness", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "HA",
          description: "Connect Harness read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Harness.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "harvest", name: "Harvest", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "HA",
          description: "Connect Harvest read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Harvest.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "helloid", name: "HelloID", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "HE",
          description: "Connect HelloID read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in HelloID.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "hootsuite", name: "Hootsuite", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "HO",
          description: "Connect Hootsuite read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Hootsuite.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "hoxhunt", name: "HoxHunt", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "HO",
          description: "Connect HoxHunt read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in HoxHunt.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ideagen", name: "Ideagen", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "ID",
          description: "Connect Ideagen read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Ideagen.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ideo", name: "Ideo", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "ID",
          description: "Connect Ideo read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Ideo.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "invision", name: "InVision", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "IN",
          description: "Connect InVision read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in InVision.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "jobadder", name: "JobAdder", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "JO",
          description: "Connect JobAdder read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in JobAdder.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "jobvite", name: "JobVite", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "JO",
          description: "Connect JobVite read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in JobVite.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "jostle", name: "Jostle", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "JO",
          description: "Connect Jostle read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Jostle.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "junojourney", name: "Juno Journey", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "JU",
          description: "Connect Juno Journey read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Juno Journey.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "keeper", name: "Keeper", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "KE",
          description: "Connect Keeper read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Keeper.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "kpifire", name: "KPI Fire", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "KP",
          description: "Connect KPI Fire read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in KPI Fire.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "lastpass", name: "LastPass", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "LA",
          description: "Connect LastPass read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in LastPass.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "lattice", name: "Lattice", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "LA",
          description: "Connect Lattice read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Lattice.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "latticeflowai", name: "LatticeFlow AI", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "LA",
          description: "Connect LatticeFlow AI read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in LatticeFlow AI.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "launchdarkly", name: "LaunchDarkly", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "LA",
          description: "Connect LaunchDarkly read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in LaunchDarkly.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "leapsome", name: "Leapsome", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "LE",
          description: "Connect Leapsome read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Leapsome.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "lever", name: "Lever", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "LE",
          description: "Connect Lever read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Lever.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "logicgate", name: "LogicGate", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "LO",
          description: "Connect LogicGate read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in LogicGate.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "loom", name: "Loom", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "LO",
          description: "Connect Loom read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Loom.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "looop", name: "Looop", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "LO",
          description: "Connect Looop read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Looop.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "lucid", name: "Lucid", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "LU",
          description: "Connect Lucid read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Lucid.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "appaegis", name: "Mammoth Cyber (Appaegis)", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "MA",
          description: "Connect Mammoth Cyber (Appaegis) read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Mammoth Cyber (Appaegis).", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "maptician", name: "Maptician", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "MA",
          description: "Connect Maptician read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Maptician.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "metapulse", name: "MetaPulse", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "ME",
          description: "Connect MetaPulse read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in MetaPulse.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "mindtickle", name: "Mindtickle", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "MI",
          description: "Connect Mindtickle read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Mindtickle.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "miro", name: "Miro", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "MI",
          description: "Connect Miro read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Miro.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "mixpanel", name: "Mixpanel", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "MI",
          description: "Connect Mixpanel read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Mixpanel.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "mode", name: "Mode", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "MO",
          description: "Connect Mode read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Mode.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "multiplier", name: "Multiplier", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "MU",
          description: "Connect Multiplier read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Multiplier.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "mypolicies", name: "myPolicies", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "MY",
          description: "Connect myPolicies read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in myPolicies.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "nordpass", name: "NordPass", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "NO",
          description: "Connect NordPass read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in NordPass.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "openai", name: "OpenAI", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "OP",
          description: "Connect OpenAI read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in OpenAI.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "opsbook", name: "OpsBook", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "OP",
          description: "Connect OpsBook read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in OpsBook.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "opsgenie1f68e", name: "Opsgenie", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "OP",
          description: "Connect Opsgenie read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Opsgenie.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "opslevel", name: "OpsLevel", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "OP",
          description: "Connect OpsLevel read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in OpsLevel.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "osano", name: "Osano", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "OS",
          description: "Connect Osano read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Osano.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "parsable", name: "Parsable", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "PA",
          description: "Connect Parsable read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Parsable.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "peakon", name: "Peakon", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "PE",
          description: "Connect Peakon read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Peakon.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "peripass", name: "Peripass", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "PE",
          description: "Connect Peripass read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Peripass.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "phalanx", name: "Phalanx", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "PH",
          description: "Connect Phalanx read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Phalanx.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "pipedrive", name: "Pipedrive", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "PI",
          description: "Connect Pipedrive read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Pipedrive.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "postman", name: "Postman", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "PO",
          description: "Connect Postman read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Postman.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "prioritymatrix", name: "Priority Matrix", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "PR",
          description: "Connect Priority Matrix read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Priority Matrix.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "probackupbv", name: "Pro Backup BV", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "PR",
          description: "Connect Pro Backup BV read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Pro Backup BV.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "prodpad", name: "Prodpad", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "PR",
          description: "Connect Prodpad read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Prodpad.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "productboard", name: "Productboard", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "PR",
          description: "Connect Productboard read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Productboard.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "qlik", name: "Qlik", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "QL",
          description: "Connect Qlik read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Qlik.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ramp", name: "Ramp", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "RA",
          description: "Connect Ramp read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Ramp.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ravenna", name: "Ravenna", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "RA",
          description: "Connect Ravenna read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Ravenna.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "reportit", name: "Report It", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "RE",
          description: "Connect Report It read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Report It.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "rfpio", name: "Responsive (RFPIO)", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "RE",
          description: "Connect Responsive (RFPIO) read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Responsive (RFPIO).", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "retool", name: "Retool", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "RE",
          description: "Connect Retool read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Retool.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "robin", name: "Robin", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "RO",
          description: "Connect Robin read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Robin.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "rockset", name: "Rockset", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "RO",
          description: "Connect Rockset read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Rockset.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "rollbar", name: "Rollbar", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "RO",
          description: "Connect Rollbar read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Rollbar.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "scaleai", name: "Scale AI", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SC",
          description: "Connect Scale AI read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Scale AI.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "securily", name: "Securily", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SE",
          description: "Connect Securily read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Securily.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "segment", name: "Segment", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SE",
          description: "Connect Segment read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Segment.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "shopify", name: "Shopify", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SH",
          description: "Connect Shopify read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Shopify.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "sigma", name: "Sigma Computing", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SI",
          description: "Connect Sigma Computing read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Sigma Computing.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "simplebackups", name: "SimpleBackups", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SI",
          description: "Connect SimpleBackups read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in SimpleBackups.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "slab", name: "Slab", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SL",
          description: "Connect Slab read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Slab.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "smartfile", name: "Smartfile", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SM",
          description: "Connect Smartfile read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Smartfile.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "smartrecruiters", name: "SmartRecruiters", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SM",
          description: "Connect SmartRecruiters read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in SmartRecruiters.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "sophos", name: "Sophos", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SO",
          description: "Connect Sophos read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Sophos.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "sparkhire", name: "Sparkhire", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SP",
          description: "Connect Sparkhire read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Sparkhire.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "speakupnow", name: "SpeakUp Now", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SP",
          description: "Connect SpeakUp Now read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in SpeakUp Now.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "splashtop", name: "Splashtop", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SP",
          description: "Connect Splashtop read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Splashtop.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "statsig", name: "Statsig", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "ST",
          description: "Connect Statsig read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Statsig.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "subrosa", name: "Subrosa", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SU",
          description: "Connect Subrosa read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Subrosa.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "supabase", name: "Supabase", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SU",
          description: "Connect Supabase read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Supabase.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "sym", name: "Sym", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "SY",
          description: "Connect Sym read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Sym.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "tailscale", name: "Tailscale", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "TA",
          description: "Connect Tailscale read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Tailscale.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "talentech", name: "Talentech", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "TA",
          description: "Connect Talentech read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Talentech.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "teamtailor", name: "Teamtailor", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "TE",
          description: "Connect Teamtailor read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Teamtailor.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "termius", name: "Termius", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "TE",
          description: "Connect Termius read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Termius.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "timetoreply", name: "timetoreply", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "TI",
          description: "Connect timetoreply read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in timetoreply.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "torq", name: "Torq", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "TO",
          description: "Connect Torq read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Torq.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "travelperk", name: "Travelperk", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "TR",
          description: "Connect Travelperk read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Travelperk.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "trelica", name: "Trelica by 1Password", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "TR",
          description: "Connect Trelica by 1Password read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Trelica by 1Password.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "tribeloo", name: "Tribeloo", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "TR",
          description: "Connect Tribeloo read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Tribeloo.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "twingate", name: "Twingate", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "TW",
          description: "Connect Twingate read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Twingate.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "unifi", name: "Unifi", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "UN",
          description: "Connect Unifi read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Unifi.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "vendr", name: "Vendr", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "VE",
          description: "Connect Vendr read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Vendr.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "vonage", name: "Vonage", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "VO",
          description: "Connect Vonage read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Vonage.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "webex", name: "WebEx", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "WE",
          description: "Connect WebEx read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in WebEx.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "whimsical", name: "Whimsical", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "WH",
          description: "Connect Whimsical read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Whimsical.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "workplace", name: "Workplace", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "WO",
          description: "Connect Workplace read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Workplace.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "xero", name: "Xero", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "XE",
          description: "Connect Xero read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Xero.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "zapier", name: "Zapier", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "ZA",
          description: "Connect Zapier read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Zapier.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "zavanta", name: "Zavanta", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "ZA",
          description: "Connect Zavanta read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Zavanta.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "zero", name: "Zero", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "ZE",
          description: "Connect Zero read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Zero.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "zip", name: "Zip", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "ZI",
          description: "Connect Zip read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Zip.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "zoominfo", name: "ZoomInfo", tagline: "Compliance evidence integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-plug", abbr: "ZO",
          description: "Connect ZoomInfo read-only to evidence the compliance evidence control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in ZoomInfo.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "Security Awareness Training",
      icon:  "fa-graduation-cap",
      vendors: [
        {
          id: "360learning", name: "360Learning", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "36",
          description: "Connect 360Learning read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in 360Learning.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "adaptivesecurity", name: "Adaptive Security", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "AD",
          description: "Connect Adaptive Security read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Adaptive Security.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "awarego", name: "AwareGo", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "AW",
          description: "Connect AwareGo read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in AwareGo.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "caniphish", name: "CanIPhish", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "CA",
          description: "Connect CanIPhish read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in CanIPhish.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "cybeready", name: "CybeReady", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "CY",
          description: "Connect CybeReady read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in CybeReady.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "cyberly", name: "Cyberly", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "CY",
          description: "Connect Cyberly read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Cyberly.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "easyllama", name: "EasyLlama", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "EA",
          description: "Connect EasyLlama read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in EasyLlama.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "elba", name: "Elba", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "EL",
          description: "Connect Elba read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Elba.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ethena", name: "Ethena", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "ET",
          description: "Connect Ethena read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Ethena.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ghosteye", name: "GhostEye", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "GH",
          description: "Connect GhostEye read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in GhostEye.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "haekka", name: "Haekka", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "HA",
          description: "Connect Haekka read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Haekka.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "hooksecurity", name: "Hook Security", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "HO",
          description: "Connect Hook Security read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Hook Security.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "curricula", name: "Huntress", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "HU",
          description: "Connect Huntress read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Huntress.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "kamae", name: "Kama\u00e9", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "KA",
          description: "Connect Kama\u00e9 read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Kama\u00e9.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "knowbe4", name: "KnowBe4", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "KN",
          description: "Connect KnowBe4 read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in KnowBe4.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "moxso", name: "Moxso", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "MO",
          description: "Connect Moxso read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Moxso.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ninjio", name: "Ninjio", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "NI",
          description: "Connect Ninjio read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Ninjio.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "outkept", name: "Outkept", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "OU",
          description: "Connect Outkept read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Outkept.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "outthink", name: "OutThink", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "OU",
          description: "Connect OutThink read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in OutThink.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ransomleak", name: "RansomLeak", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "RA",
          description: "Connect RansomLeak read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in RansomLeak.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "riot", name: "Riot", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "RI",
          description: "Connect Riot read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Riot.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "safestack", name: "SafeStack", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "SA",
          description: "Connect SafeStack read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in SafeStack.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "smartfense", name: "Smartfense", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "SM",
          description: "Connect Smartfense read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Smartfense.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "sosafe", name: "SoSafe", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "SO",
          description: "Connect SoSafe read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in SoSafe.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "succeedlearn", name: "SucceedLEARN by Succeed Technologies", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "SU",
          description: "Connect SucceedLEARN by Succeed Technologies read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in SucceedLEARN by Succeed Technologies.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "talentlms", name: "TalentLMS", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "TA",
          description: "Connect TalentLMS read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in TalentLMS.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "vansec", name: "Vansec", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "VA",
          description: "Connect Vansec read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Vansec.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "wizer", name: "Wizer", tagline: "Security training integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-graduation-cap", abbr: "WI",
          description: "Connect Wizer read-only to evidence the security training control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Wizer.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "Task & Change Management",
      icon:  "fa-tasks",
      vendors: [
        {
          id: "aha", name: "Aha!", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "AH",
          description: "Connect Aha! read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Aha!.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "asana", name: "Asana", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "AS",
          description: "Connect Asana read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Asana.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "basecamp", name: "Basecamp", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "BA",
          description: "Connect Basecamp read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Basecamp.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "clickup", name: "ClickUp", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "CL",
          description: "Connect ClickUp read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in ClickUp.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "dixa", name: "Dixa", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "DI",
          description: "Connect Dixa read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Dixa.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "freshdesk", name: "Freshdesk", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "FR",
          description: "Connect Freshdesk read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Freshdesk.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "freshservice", name: "freshservice", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "FR",
          description: "Connect freshservice read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in freshservice.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "front", name: "Front", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "FR",
          description: "Connect Front read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Front.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "githubissues", name: "Github Issues", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "GI",
          description: "Connect Github Issues read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Github Issues.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "gitlabissues", name: "GitLab Issues", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "GI",
          description: "Connect GitLab Issues read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in GitLab Issues.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "gitlabissuesonprem", name: "GitLab Issues (On-Prem)", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "GI",
          description: "Connect GitLab Issues (On-Prem) read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in GitLab Issues (On-Prem).", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "gladly", name: "Gladly", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "GL",
          description: "Connect Gladly read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Gladly.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "gorgias", name: "Gorgias", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "GO",
          description: "Connect Gorgias read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Gorgias.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "height", name: "Height", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "HE",
          description: "Connect Height read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Height.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "helpscout", name: "Help Scout", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "HE",
          description: "Connect Help Scout read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Help Scout.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "hive", name: "Hive", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "HI",
          description: "Connect Hive read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Hive.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "humi", name: "Humi", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "HU",
          description: "Connect Humi read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Humi.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "kustomer", name: "Kustomer", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "KU",
          description: "Connect Kustomer read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Kustomer.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "linear", name: "Linear", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "LI",
          description: "Connect Linear read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Linear.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "microsoftplanner", name: "Microsoft Planner", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "MI",
          description: "Connect Microsoft Planner read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Microsoft Planner.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "monday", name: "Monday", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "MO",
          description: "Connect Monday read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Monday.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "pivotaltracker", name: "Pivotal Tracker", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "PI",
          description: "Connect Pivotal Tracker read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Pivotal Tracker.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "reamaze", name: "Re:amaze", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "RE",
          description: "Connect Re:amaze read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Re:amaze.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "shortcut", name: "Shortcut", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "SH",
          description: "Connect Shortcut read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Shortcut.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "smartsheet", name: "Smartsheet", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "SM",
          description: "Connect Smartsheet read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Smartsheet.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "spotdraft", name: "Spotdraft", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "SP",
          description: "Connect Spotdraft read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Spotdraft.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "taskp3", name: "TaskP3 by Raidon, Inc", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "TA",
          description: "Connect TaskP3 by Raidon, Inc read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in TaskP3 by Raidon, Inc.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "teamwork", name: "Teamwork", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "TE",
          description: "Connect Teamwork read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Teamwork.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "trello", name: "Trello", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "TR",
          description: "Connect Trello read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Trello.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "wrike", name: "Wrike", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "WR",
          description: "Connect Wrike read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Wrike.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "youtrack", name: "YouTrack", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "YO",
          description: "Connect YouTrack read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in YouTrack.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "zohobugtracker", name: "Zoho BugTracker", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "ZO",
          description: "Connect Zoho BugTracker read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Zoho BugTracker.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "zohodesk", name: "Zoho Desk", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "ZO",
          description: "Connect Zoho Desk read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Zoho Desk.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "zohopeople", name: "Zoho People", tagline: "Task / ticketing integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-tasks", abbr: "ZO",
          description: "Connect Zoho People read-only to evidence the task / ticketing control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Zoho People.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "More Version Control",
      icon:  "fa-code-branch",
      vendors: [
        {
          id: "awscodecommit", name: "AWS CodeCommit", tagline: "Source control integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-code-branch", abbr: "AW",
          description: "Connect AWS CodeCommit read-only to evidence the source control control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in AWS CodeCommit.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "azuredevops", name: "Azure DevOps", tagline: "Source control integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-code-branch", abbr: "AZ",
          description: "Connect Azure DevOps read-only to evidence the source control control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Azure DevOps.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "githubonprem", name: "GitHub On-Prem", tagline: "Source control integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-code-branch", abbr: "GI",
          description: "Connect GitHub On-Prem read-only to evidence the source control control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in GitHub On-Prem.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "gitlabonprem", name: "GitLab (On-Prem)", tagline: "Source control integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-code-branch", abbr: "GI",
          description: "Connect GitLab (On-Prem) read-only to evidence the source control control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in GitLab (On-Prem).", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
    {
      label: "Vulnerability Management",
      icon:  "fa-bug",
      vendors: [
        {
          id: "13security", name: "13 Security", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "13",
          description: "Connect 13 Security read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in 13 Security.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "aftra", name: "Aftra", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "AF",
          description: "Connect Aftra read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Aftra.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "aikido", name: "Aikido", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "AI",
          description: "Connect Aikido read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Aikido.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "almanax", name: "Almanax", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "AL",
          description: "Connect Almanax read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Almanax.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "astrasecurity", name: "Astra Security", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "AS",
          description: "Connect Astra Security read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Astra Security.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "attackerview", name: "Attackerview", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "AT",
          description: "Connect Attackerview read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Attackerview.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "awsguardduty", name: "AWS GuardDuty", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "AW",
          description: "Connect AWS GuardDuty read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in AWS GuardDuty.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "awsinspector", name: "AWS Inspector", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "AW",
          description: "Connect AWS Inspector read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in AWS Inspector.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "azuredefender", name: "Azure Defender", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "AZ",
          description: "Connect Azure Defender read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Azure Defender.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "blacklocksecurity", name: "Blacklock Security", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "BL",
          description: "Connect Blacklock Security read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Blacklock Security.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "botprise", name: "Botprise", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "BO",
          description: "Connect Botprise read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Botprise.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "breachlock", name: "BreachLock Inc.", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "BR",
          description: "Connect BreachLock Inc. read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in BreachLock Inc..", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "cacilian", name: "Cacilian LLC- A Prescient Security Company", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "CA",
          description: "Connect Cacilian LLC- A Prescient Security Company read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Cacilian LLC- A Prescient Security Company.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "capturethebug", name: "Capture the Bug", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "CA",
          description: "Connect Capture the Bug read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Capture the Bug.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "coana", name: "Coana", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "CO",
          description: "Connect Coana read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Coana.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "cobalt", name: "Cobalt Labs", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "CO",
          description: "Connect Cobalt Labs read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Cobalt Labs.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "coguard", name: "Coguard", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "CO",
          description: "Connect Coguard read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Coguard.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "contxt", name: "Contxt", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "CO",
          description: "Connect Contxt read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Contxt.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "deepsource", name: "DeepSource", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "DE",
          description: "Connect DeepSource read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in DeepSource.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "edgebit", name: "EdgeBit", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "ED",
          description: "Connect EdgeBit read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in EdgeBit.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "fencer", name: "Fencer", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "FE",
          description: "Connect Fencer read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Fencer.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "gcpcontaineranalysis", name: "GCP Container Analysis", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "GC",
          description: "Connect GCP Container Analysis read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in GCP Container Analysis.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "githubdependabot", name: "Github Dependabot", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "GI",
          description: "Connect Github Dependabot read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Github Dependabot.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "halosecurity", name: "Halo Security", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "HA",
          description: "Connect Halo Security read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Halo Security.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "heyhack", name: "HeyHack", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "HE",
          description: "Connect HeyHack read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in HeyHack.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "hostedscan", name: "HostedScan", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "HO",
          description: "Connect HostedScan read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in HostedScan.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "intruder", name: "Intruder", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "IN",
          description: "Connect Intruder read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Intruder.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "jitsecurity", name: "Jit", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "JI",
          description: "Connect Jit read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Jit.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "lacework", name: "Lacework", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "LA",
          description: "Connect Lacework read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Lacework.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "lumenovaai", name: "Lumenova", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "LU",
          description: "Connect Lumenova read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Lumenova.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "defendergcchigh", name: "Microsoft Defender (GCC High)", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "MI",
          description: "Connect Microsoft Defender (GCC High) read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Microsoft Defender (GCC High).", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "defenderforcloud", name: "Microsoft Defender for Cloud", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "MI",
          description: "Connect Microsoft Defender for Cloud read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Microsoft Defender for Cloud.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "orca", name: "Orca", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "OR",
          description: "Connect Orca read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Orca.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "ostorlab", name: "Ostorlab", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "OS",
          description: "Connect Ostorlab read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Ostorlab.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "panopticscans", name: "Panoptic Scans", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "PA",
          description: "Connect Panoptic Scans read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Panoptic Scans.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "pentesttools", name: "Pentest-Tools.com", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "PE",
          description: "Connect Pentest-Tools.com read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Pentest-Tools.com.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "plerion", name: "Plerion", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "PL",
          description: "Connect Plerion read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Plerion.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "prancer", name: "Prancer", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "PR",
          description: "Connect Prancer read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Prancer.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "probely", name: "Probely", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "PR",
          description: "Connect Probely read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Probely.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "qualys", name: "Qualys", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "QU",
          description: "Connect Qualys read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Qualys.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "rapid7", name: "Rapid7", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "RA",
          description: "Connect Rapid7 read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Rapid7.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "sentinelone", name: "SentinelOne", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "SE",
          description: "Connect SentinelOne read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in SentinelOne.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "snyk", name: "Snyk", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "SN",
          description: "Connect Snyk read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Snyk.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "socket", name: "Socket", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "SO",
          description: "Connect Socket read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Socket.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "softwaresecured", name: "Software Secured", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "SO",
          description: "Connect Software Secured read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Software Secured.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "soos", name: "SOOS", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "SO",
          description: "Connect SOOS read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in SOOS.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "stackhawk", name: "StackHawk", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "ST",
          description: "Connect StackHawk read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in StackHawk.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "strikesecurity", name: "Strike Security", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "ST",
          description: "Connect Strike Security read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Strike Security.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "tenablevulnmgmt", name: "Tenable Vulnerability Management", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "TE",
          description: "Connect Tenable Vulnerability Management read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Tenable Vulnerability Management.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "threatmate", name: "Threatmate", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "TH",
          description: "Connect Threatmate read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Threatmate.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "trava", name: "Trava", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "TR",
          description: "Connect Trava read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Trava.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "wiz", name: "Wiz", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "WI",
          description: "Connect Wiz read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Wiz.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
        {
          id: "wizforgoverment", name: "Wiz for Goverment", tagline: "Vulnerability scanner integration",
          accent: '#64748B', bg: 'rgba(100,116,139,0.06)', faFallback: "fa-bug", abbr: "WI",
          description: "Connect Wiz for Goverment read-only to evidence the vulnerability scanner control in your audits.",
          assets: [{ icon: 'fa-plug', label: 'Connection' }, { icon: 'fa-clipboard-check', label: 'Control evidence' }],
          steps: ["Create a read-only API key or token in Wiz for Goverment.", 'Paste it below to connect — CATAAM reads configuration metadata only.'],
        },
      ],
    },
  ];

  /* Flatten all vendors into a lookup map */
  const VENDOR_MAP = {};
  CATEGORIES.forEach(cat => cat.vendors.forEach(v => { VENDOR_MAP[v.id] = v; }));

  // Stash CPA/CISO client context for the wizard route to read. Cleared by the wizard
  // after submission so subsequent OrgAdmin flows are not affected.
  useEffect(() => {
    if (orgContext?.id) {
      sessionStorage.setItem('cpaClientContext', JSON.stringify({
        orgId:   orgContext.id,
        orgName: orgContext.name || '',
      }));
    } else {
      sessionStorage.removeItem('cpaClientContext');
    }
  }, [orgContext?.id, orgContext?.name]);

  // Bring the detail panel into view whenever a card is selected (it lives at the top of the
  // layout as a sticky aside; without this, selecting a card at the bottom shows nothing).
  useEffect(() => {
    if (selected && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selected]);


  const handleCardClick = (vendor) => {
    if (vendor.comingSoon) return;
    setSelected(vendor);
    setGithubError('');
  };

  const handleGitHubConnect = async () => {
    // In CPA mode, the org name is the client org's; in OrgAdmin mode, fall back to logged-in org
    const orgName = orgContext?.name || localStorage.getItem('organization');
    if (!orgName || orgName.trim() === '') {
      setGithubError(t('VendorCards.errorOrgNameNotFound'));
      return;
    }
    // /github/connect and /github/callback are necessarily permitAll (GitHub's OAuth redirect chain
    // carries no app JWT), so `state` must never be a raw, client-constructed org name/id again --
    // that was a cross-org VendorConnection-write IDOR (T-0670/T-0710). Mint a short-lived signed
    // token through the ONE authenticated step in this flow instead; /github/connect + /github/
    // callback only ever trust what verifiably decodes out of it.
    let state;
    try {
      const params = orgContext?.id ? { clientOrgId: orgContext.id } : {};
      const resp = await axios.get('/github/connect-state', { params });
      state = resp.data.state;
    } catch (err) {
      setGithubError(t('VendorCards.errorConnectStateFetchFailed'));
      return;
    }
    // Route through the backend's /github/connect, which redirects to GitHub's user-authorization
    // endpoint. That ALWAYS returns to our callback (even when the app is already installed on the
    // account), so reconnecting works — unlike linking straight to the install page, which only
    // redirects back on a FIRST install (a reconnect is an "update" GitHub won't redirect on unless
    // "Redirect on update" is set, so it silently dead-ended on github.com). /github/connect lives at
    // the service root, not under /api; client_id stays server-side.
    const serviceOrigin = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api\/?$/, '');
    window.location.href = `${serviceOrigin}/github/connect?state=${encodeURIComponent(state)}`;
  };

  return (
    <div className="vc-page" style={embedded ? { padding: 0, background: 'transparent' } : undefined}>
      {orgContext?.id && (
        <div style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
          border: '1px solid #818cf8', borderRadius: 10, padding: '10px 14px', marginBottom: 14,
          fontSize: '0.86rem', color: '#3730a3', display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="fas fa-user-tie" />
          <span>{t('VendorCards.cpaBannerBefore')} <strong>{orgContext.name}</strong>{t('VendorCards.cpaBannerAfter')}</span>
        </div>
      )}
      <GithubConnectError />
      <div className="vc-layout">
        {/* ── Grid area ──────────────────────────── */}
        <div className="vc-grid-area">
          <div className="vc-grid-header">
            <div className="vc-grid-title">{t('VendorCards.gridTitle')}</div>
            <div className="vc-grid-sub">{t('VendorCards.gridSub')}</div>
          </div>

          {CATEGORIES.map(cat => (
            <div key={cat.label} className="vc-category">
              <div className="vc-cat-label">
                <i className={`fas ${cat.icon}`} />
                {cat.label}
              </div>
              <div className="vc-cat-grid">
                {cat.vendors.map(vendor => (
                  <button
                    key={vendor.id}
                    className={[
                      'vc-card',
                      selected?.id === vendor.id ? 'vc-card-active' : '',
                      vendor.comingSoon ? 'vc-card-soon' : '',
                    ].filter(Boolean).join(' ')}
                    style={{ '--accent': vendor.accent, '--bg': vendor.bg }}
                    onClick={() => handleCardClick(vendor)}
                    disabled={vendor.comingSoon}
                    title={vendor.comingSoon ? t('VendorCards.cardComingSoonTitle', { name: vendor.name }) : vendor.name}
                  >
                    <div className="vc-card-logo-wrap">
                      <VendorIcon vendor={vendor} size="card" />
                    </div>
                    <div className="vc-card-name">{vendor.name}</div>
                    {vendor.comingSoon && <span className="vc-soon-badge">{t('VendorCards.soonBadge')}</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ── Detail panel ───────────────────────── */}
        {selected ? (
          <aside className="vc-panel" key={selected.id} ref={panelRef}>
            <button className="vc-panel-close" onClick={() => setSelected(null)} aria-label={t('VendorCards.closeAria')}>
              <i className="fas fa-times" />
            </button>

            <div className="vc-panel-header">
              <div className="vc-panel-logo-wrap">
                <VendorIcon vendor={selected} size="panel" />
              </div>
              <div>
                <div className="vc-panel-name">{selected.name}</div>
                <div className="vc-panel-tagline">{selected.tagline}</div>
              </div>
            </div>

            <p className="vc-panel-desc">{selected.description}</p>

            <div className="vc-section">
              <div className="vc-section-title">
                <i className="fas fa-layer-group" /> {t('VendorCards.sectionAssets')}
              </div>
              <ul className="vc-asset-list">
                {selected.assets.map(a => (
                  <li key={a.label}>
                    <span className="vc-asset-icon-wrap" style={{ background: selected.accent + '15', color: selected.accent }}>
                      <i className={`fas ${a.icon}`} />
                    </span>
                    {a.label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="vc-section">
              <div className="vc-section-title">
                <i className="fas fa-list-ol" /> {t('VendorCards.sectionHowToConnect')}
              </div>
              <ol className="vc-how-list">
                {selected.steps.map((s, i) => (
                  <li key={i}>
                    <span className="vc-how-num" style={{ background: selected.accent + '15', color: selected.accent }}>
                      {i + 1}
                    </span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="vc-panel-cta">
              {selected.oauth ? (
                <>
                  {githubError && (
                    <div className="vc-error-msg">
                      <i className="fas fa-exclamation-triangle" /> {githubError}
                    </div>
                  )}
                  <a
                    href="https://github.com/settings/organizations"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vc-secondary-btn"
                  >
                    <i className="fab fa-github" /> {t('VendorCards.openGithubOrgSettings')}
                  </a>
                  <button
                    className="vc-primary-btn"
                    style={{ background: selected.accent }}
                    onClick={handleGitHubConnect}
                  >
                    <i className="fab fa-github" /> {t('VendorCards.connectViaGithub')}
                  </button>
                </>
              ) : (
                <button
                  className="vc-primary-btn"
                  style={{ background: selected.accent }}
                  onClick={() => navigate(`/dashboard/vendors/cards/${selected.id}`)}
                >
                  {t('VendorCards.startSetup')} <i className="fas fa-arrow-right" />
                </button>
              )}
            </div>
          </aside>
        ) : (
          <div className="vc-panel-empty">
            <i className="fas fa-hand-pointer vc-empty-icon" />
            <div className="vc-empty-title">{t('VendorCards.emptyTitle')}</div>
            <div className="vc-empty-sub">{t('VendorCards.emptySub')}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VendorCards;
